import { StrictMode, useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from "recharts";
import type { FormEvent, ReactNode } from "react";
import { createRoot } from "react-dom/client";
import {
  Activity,
  ArrowRight,
  Bell,
  BookmarkCheck,
  BookOpen,
  Bot,
  CalendarClock,
  Check,
  ChevronDown,
  CheckCircle2,
  Clock3,
  CreditCard,
  Gauge,
  History,
  LockKeyhole,
  LogIn,
  LogOut,
  MessageCircle,
  Pin,
  PlusCircle,
  Search,
  Send,
  Server as ServerIcon,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Trash2,
  Upload,
  X,
  Zap,
} from "lucide-react";
import {
  ApiError,
  askKodama,
  createBillingPortalSession,
  createCheckoutSession,
  createLore,
  deleteLore,
  exchangeOAuthCode,
  getAdminServers,
  getAdminStats,
  getMe,
  getServer,
  getStoredToken,
  initiateLogin,
  logout,
  scanServerHistory,
  fetchServerChannels,
  storeToken,
  updateSettings,
  dismissReport,
  promoteReport,
  type AdminServer,
  type AdminStats,
  type AuditEvent,
  type AskMessage,
  type GuildChannel,
  type LoreEntry,
  type MeResponse,
  type ManageableGuild,
  type ServerDetail,
  type ServerReport,
  type ServerSummary,
  type SettingsPatch,
} from "./api";
import "./styles.css";

const marketingOrigin = "https://askkodama.com";

type TabId =
  | "overview"
  | "memory"
  | "recaps"
  | "privacy"
  | "settings"
  | "profile";

type PageId =
  | "home"
  | "servers"
  | "dashboard"
  | "billing"
  | "info"
  | "commands"
  | "tos"
  | "privacy-policy"
  | "auth-finish"
  | "oauth-callback"
  | "admin"
  | "usage";

const defaultBotAvatarUrl = "/default_bot_pfp.png";
const defaultBannerUrl = "/default_banner.png";
const maxAvatarBytes = 1024 * 1024;

const tabs: Array<{ id: TabId; label: string; icon: typeof Gauge }> = [
  { id: "overview", label: "Overview", icon: Gauge },
  { id: "memory", label: "Memory", icon: Search },
  { id: "profile", label: "Bot Profile", icon: Bot },
  { id: "recaps", label: "Schedule", icon: CalendarClock },
  { id: "settings", label: "Controls", icon: SlidersHorizontal },
  { id: "privacy", label: "Guardrails", icon: ShieldCheck },
];

function botInviteUrl(guildId?: string): string {
  const params = new URLSearchParams({
    client_id: "1508256939547561995",
    permissions: "67456064", // View Channel + Read History + Send Messages + Add Reactions + Embed Links + External Emojis + Change Nickname
    scope: "bot applications.commands",
  });
  if (guildId) {
    params.set("guild_id", guildId);
    params.set("disable_guild_select", "true");
  }
  return `https://discord.com/oauth2/authorize?${params.toString()}`;
}

function currentReturnTo(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function getInitialTab(): TabId {
  const hash = window.location.hash.replace("#", "");
  return tabs.some((tab) => tab.id === hash) ? (hash as TabId) : "overview";
}

function getInitialPage(): PageId {
  if (window.location.pathname.startsWith("/auth/finish")) return "auth-finish";
  // Discord OAuth callback lands here: /api/auth/callback?code=X&state=Y
  if (window.location.pathname.startsWith("/api/auth/callback"))
    return "oauth-callback";
  if (window.location.pathname.startsWith("/commands")) return "commands";
  if (window.location.pathname.startsWith("/info")) return "info";
  if (window.location.pathname.startsWith("/tos")) return "tos";
  if (window.location.pathname.startsWith("/privacy")) return "privacy-policy";
  if (window.location.pathname.startsWith("/billing")) return "billing";
  if (window.location.pathname.startsWith("/admin")) return "admin";
  if (window.location.pathname.startsWith("/usage")) return "usage";
  if (window.location.pathname.startsWith("/servers")) return "servers";
  if (window.location.pathname.startsWith("/dashboard")) return "dashboard";
  return "home";
}

function formatDate(value: string | null): string {
  if (!value) return "No messages yet";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatAction(action: string): string {
  return action
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatCategory(category: string | null): string {
  if (!category || category === "inside_joke" || category === "moment" || category === "lore") return "Lore";
  return formatAction(category);
}

// ---------------------------------------------------------------------------
// ConfirmModal — reusable in-app confirmation dialog (replaces window.confirm)
// ---------------------------------------------------------------------------
function ConfirmModal({
  message,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  danger = true,
  onConfirm,
  onCancel,
}: {
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  // Close on backdrop click
  function handleBackdrop(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onCancel();
  }
  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="confirm-backdrop" onClick={handleBackdrop}>
      <div className="confirm-modal" role="dialog" aria-modal="true">
        <p className="confirm-message">{message}</p>
        <div className="confirm-actions">
          <button type="button" className="secondary-button" onClick={onCancel}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={danger ? "danger-button" : "primary-button"}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      {icon ?? <Bot size={24} aria-hidden="true" />}
      <h3>{title}</h3>
      <p>{body}</p>
      {action}
    </div>
  );
}

function Toast({
  message,
  icon,
  tone = "neutral",
  dismissing,
  onDismiss,
}: {
  message: string;
  icon?: ReactNode;
  tone?: "neutral" | "success" | "error";
  dismissing: boolean;
  onDismiss: () => void;
}) {
  const toneClass = tone === "neutral" ? "" : ` toast-${tone}`;
  return (
    <button
      className={`toast${toneClass}${dismissing ? " toast-leaving" : ""}`}
      type="button"
      role="alert"
      onClick={onDismiss}
    >
      {icon}
      <span>{message}</span>
    </button>
  );
}

function AuthErrorToast({
  dismissing,
  onDismiss,
}: {
  dismissing: boolean;
  onDismiss: () => void;
}) {
  return (
    <Toast
      message="Discord sign-in could not start. Check that the dashboard API URL is live and allowed by CORS, then try again."
      tone="error"
      icon={<LogIn size={18} aria-hidden="true" />}
      dismissing={dismissing}
      onDismiss={onDismiss}
    />
  );
}

function ServerSelect({
  servers,
  selectedId,
  onChange,
}: {
  servers: ServerSummary[];
  selectedId: string | null;
  onChange: (serverId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [renderMenu, setRenderMenu] = useState(false);
  const [closing, setClosing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const selected = servers.find((server) => server.id === selectedId);

  function openMenu(): void {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setRenderMenu(true);
    setClosing(false);
    setOpen(true);
  }

  function closeMenu(): void {
    setOpen(false);
    setClosing(true);
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => {
      setRenderMenu(false);
      setClosing(false);
      closeTimerRef.current = null;
    }, 160);
  }

  function toggleMenu(): void {
    if (open) {
      closeMenu();
    } else {
      openMenu();
    }
  }

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        closeMenu();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    };
  });

  return (
    <div ref={ref} className="server-picker">
      <span className="server-picker-label">Server</span>
      <button
        className="server-picker-trigger"
        type="button"
        aria-expanded={open}
        onClick={toggleMenu}
      >
        <span className="server-avatar" aria-hidden="true">
          {selected?.icon_url ? (
            <img src={selected.icon_url} alt="" />
          ) : (
            (selected?.name ?? "K").slice(0, 2).toUpperCase()
          )}
        </span>
        <strong>{selected?.name ?? "Select server"}</strong>
        <ChevronDown
          size={18}
          aria-hidden="true"
          className={open ? "chevron-open" : ""}
        />
      </button>
      {renderMenu && (
        <div
          className={`server-picker-menu dropdown-menu ${
            closing ? "dropdown-closing" : "dropdown-opening"
          }`}
          role="listbox"
        >
          {servers.map((server) => (
            <button
              className={`server-picker-option ${
                server.id === selectedId ? "active" : ""
              }`}
              key={server.id}
              type="button"
              role="option"
              aria-selected={server.id === selectedId}
              onClick={() => {
                onChange(server.id);
                closeMenu();
              }}
            >
              <span className="server-avatar" aria-hidden="true">
                {server.icon_url ? (
                  <img src={server.icon_url} alt="" />
                ) : (
                  server.name.slice(0, 2).toUpperCase()
                )}
              </span>
              <span>
                <strong>{server.name}</strong>
                <small>{server.personality}</small>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

type SelectOption = {
  value: string;
  label: string;
  detail?: string;
};

function CustomSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [renderMenu, setRenderMenu] = useState(false);
  const [closing, setClosing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const selected =
    options.find((option) => option.value === value) ?? options[0];

  function openMenu(): void {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setRenderMenu(true);
    setClosing(false);
    setOpen(true);
  }

  function closeMenu(): void {
    setOpen(false);
    setClosing(true);
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => {
      setRenderMenu(false);
      setClosing(false);
      closeTimerRef.current = null;
    }, 160);
  }

  function toggleMenu(): void {
    if (open) {
      closeMenu();
    } else {
      openMenu();
    }
  }

  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        closeMenu();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    };
  });

  return (
    <div ref={ref} className="custom-select">
      <button
        className="custom-select-trigger"
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={toggleMenu}
      >
        <span>
          <strong>{selected?.label ?? "Select"}</strong>
          {selected?.detail && <small>{selected.detail}</small>}
        </span>
        <ChevronDown
          size={18}
          aria-hidden="true"
          className={open ? "chevron-open" : ""}
        />
      </button>
      {renderMenu && (
        <div
          className={`custom-select-menu dropdown-menu ${
            closing ? "dropdown-closing" : "dropdown-opening"
          }`}
          role="listbox"
        >
          {options.map((option) => (
            <button
              className={`custom-select-option ${
                option.value === value ? "active" : ""
              }`}
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              onClick={() => {
                onChange(option.value);
                closeMenu();
              }}
            >
              <strong>{option.label}</strong>
              {option.detail && <small>{option.detail}</small>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  detail,
  tone = "neutral",
}: {
  label: string;
  value: string;
  detail: string;
  tone?: "neutral" | "good" | "warn";
}) {
  return (
    <article className={`metric tone-${tone}`}>
      <p>{label}</p>
      <strong>{value}</strong>
      <span>{detail}</span>
    </article>
  );
}

function SectionHeader({
  eyebrow,
  title,
  aside,
}: {
  eyebrow: string;
  title: string;
  aside?: ReactNode;
}) {
  return (
    <div className="panel-header">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h3>{title}</h3>
      </div>
      {aside}
    </div>
  );
}

function StatusPill({
  children,
  tone = "good",
}: {
  children: ReactNode;
  tone?: "good" | "muted" | "warn";
}) {
  return <span className={`status-pill status-${tone}`}>{children}</span>;
}

function meetsRequiredTier(
  current: string,
  required: "index" | "chronicle",
): boolean {
  if (required === "index") return current === "index" || current === "chronicle";
  return current === "chronicle";
}

function LockedSection({
  requiredTier,
  currentTier,
  onUpgrade,
  children,
}: {
  requiredTier: "index" | "chronicle";
  currentTier: string;
  onUpgrade: () => void;
  children: ReactNode;
}) {
  if (meetsRequiredTier(currentTier, requiredTier)) return <>{children}</>;
  const tierLabel = requiredTier === "index" ? "Kodama Index" : "Kodama Chronicle";
  return (
    <div className="lockable-section">
      <div className="lockable-content" aria-hidden="true">
        {children}
      </div>
      <button
        type="button"
        className="locked-overlay"
        onClick={onUpgrade}
        aria-label={`Upgrade to ${tierLabel} to unlock`}
      >
        <LockKeyhole size={28} aria-hidden="true" />
        <strong>{tierLabel}</strong>
        <span>See plans</span>
      </button>
    </div>
  );
}

function QuickAction({
  icon,
  label,
  detail,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  detail: string;
  onClick: () => void;
}) {
  return (
    <button className="quick-action" type="button" onClick={onClick}>
      <span>{icon}</span>
      <strong>{label}</strong>
      <small>{detail}</small>
    </button>
  );
}

function BrandBlock({
  label = "Dashboard",
  onNavigate,
}: {
  label?: string;
  onNavigate?: () => void;
}) {
  return (
    <button
      type="button"
      className="brand brand-button-reset"
      onClick={onNavigate}
      style={{
        cursor: onNavigate ? "pointer" : "default",
        background: "none",
        border: "none",
        padding: 0,
        textAlign: "left",
      }}
    >
      <span className="brand-mark">
        <img src="/kodama_logo.png" alt="Kodama home" />
      </span>
      <div>
        <p className="eyebrow">Kodama</p>
        <h1>{label}</h1>
      </div>
    </button>
  );
}

function UserMenu({
  displayName,
  avatarUrl,
  isAdmin,
  onNavigate,
}: {
  displayName: string;
  avatarUrl: string | null;
  isAdmin?: boolean;
  onNavigate: (page: PageId) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={ref} className="user-menu-root">
      <button
        type="button"
        className="user-menu-trigger"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="" aria-hidden="true" />
        ) : (
          <span aria-hidden="true">{displayName.slice(0, 1)}</span>
        )}
        <strong>{displayName}</strong>
        <ChevronDown
          size={15}
          aria-hidden="true"
          className={open ? "chevron-open" : ""}
        />
      </button>
      {open && (
        <div className="user-menu-dropdown">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onNavigate("servers");
            }}
          >
            <Bot size={15} aria-hidden="true" />
            My servers
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onNavigate("billing");
            }}
          >
            <CreditCard size={15} aria-hidden="true" />
            Billing
          </button>
          {isAdmin && (
            <>
              <div className="user-menu-divider" />
              <button
                type="button"
                className="user-menu-admin"
                onClick={() => {
                  setOpen(false);
                  onNavigate("admin");
                }}
              >
                <ShieldCheck size={15} aria-hidden="true" />
                Admin panel
              </button>
              <button
                type="button"
                className="user-menu-admin"
                onClick={() => {
                  setOpen(false);
                  onNavigate("usage");
                }}
              >
                <Activity size={15} aria-hidden="true" />
                Token usage
              </button>
            </>
          )}
          <div className="user-menu-divider" />
          <button
            type="button"
            className="user-menu-signout"
            onClick={() => logout().then(() => window.location.reload())}
          >
            <LogOut size={15} aria-hidden="true" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function MarketingNav({
  me,
  onNavigate,
}: {
  me: MeResponse | null;
  onNavigate: (page: PageId) => void;
}) {
  const displayName = me?.user.global_name ?? me?.user.username ?? "User";
  const avatarUrl = me?.user.avatar
    ? `https://cdn.discordapp.com/avatars/${me.user.id}/${me.user.avatar}.png?size=64`
    : null;

  return (
    <header className="marketing-nav">
      <button
        className="brand-button"
        type="button"
        onClick={() => onNavigate("home")}
      >
        <span className="public-brand">
          <img src="/kodama_logo.png" alt="" aria-hidden="true" />
          <strong>Kodama</strong>
        </span>
      </button>
      <nav aria-label="Public navigation">
        <button type="button" onClick={() => onNavigate("commands")}>
          <BookOpen size={17} aria-hidden="true" />
          Commands
        </button>
        <button type="button" onClick={() => onNavigate("info")}>
          <Activity size={17} aria-hidden="true" />
          Status
        </button>
        <a href={botInviteUrl()}>
          <PlusCircle size={17} aria-hidden="true" />
          Invite
        </a>
      </nav>
      <div className="marketing-user">
        {me ? (
          <UserMenu
            displayName={displayName}
            avatarUrl={avatarUrl}
            isAdmin={me.isAdmin}
            onNavigate={onNavigate}
          />
        ) : (
          <button type="button" onClick={() => void initiateLogin("/servers")}>
            <LogIn size={17} aria-hidden="true" />
            Sign in
          </button>
        )}
      </div>
    </header>
  );
}

function DiscordMockup() {
  return (
    <div className="discord-mockup" aria-hidden="true">
      <div className="dm-titlebar">
        <span className="dm-dot" />
        <span className="dm-dot" />
        <span className="dm-dot" />
        <span className="dm-titlebar-name"># general</span>
      </div>
      <div className="dm-header">
        <span className="dm-channel-hash">#</span>
        <span className="dm-channel-name">general</span>
        <span className="dm-header-sep">|</span>
        <span className="dm-header-topic">server hub</span>
      </div>
      <div className="dm-messages">
        <div className="dm-message">
          <div className="dm-avatar dm-avatar-purple">xS</div>
          <div className="dm-body">
            <span className="dm-name dm-name-purple">xSkull_Reign</span>
            <span className="dm-ts">Today at 4:12 PM</span>
            <div className="dm-text">anyone remember who won the s1 tourney lol</div>
          </div>
        </div>
        <div className="dm-message">
          <div className="dm-avatar dm-avatar-red">jd</div>
          <div className="dm-body">
            <span className="dm-name dm-name-red">jake_dev</span>
            <span className="dm-ts">Today at 4:13 PM</span>
            <div className="dm-text">no idea bro that was forever ago 😭</div>
          </div>
        </div>
        <div className="dm-message dm-kodama-msg">
          <div className="dm-avatar dm-avatar-kodama">
            <img src="/default_bot_pfp.png" alt="" />
          </div>
          <div className="dm-body">
            <span className="dm-name dm-name-kodama">
              Kodama
              <span className="dm-bot-tag">BOT</span>
            </span>
            <span className="dm-ts">Today at 4:13 PM</span>
            <div className="dm-text">
              That was Ryder — won the finals in OT after a 3-game comeback. You guys called it "the greatest upset in server history" 📌
            </div>
          </div>
        </div>
        <div className="dm-message">
          <div className="dm-avatar dm-avatar-purple">xS</div>
          <div className="dm-body">
            <span className="dm-name dm-name-purple">xSkull_Reign</span>
            <span className="dm-ts">Today at 4:14 PM</span>
            <div className="dm-text">💀 kodama never forgets</div>
          </div>
        </div>
        <div className="dm-slash-row">
          <span className="dm-slash-icon">/</span>
          <span className="dm-slash-cmd">lore search</span>
          <span className="dm-slash-arg">tournament winner</span>
        </div>
      </div>
    </div>
  );
}

function MarketingFooter({
  onNavigate,
}: {
  onNavigate: (page: PageId) => void;
}) {
  return (
    <footer className="marketing-footer">
      <div className="footer-brand">
        <span className="public-brand footer-brand-mark">
          <img src="/kodama_logo.png" alt="" aria-hidden="true" />
          <strong>Kodama</strong>
        </span>
        <p>Your server's lore lives on.</p>
        <p className="footer-copy-line">
          &copy; {new Date().getFullYear()} Kodama. All rights reserved.
        </p>
      </div>
      <nav className="footer-links" aria-label="Footer navigation">
        <div>
          <h4>Product</h4>
          <button type="button" onClick={() => onNavigate("commands")}>
            Commands
          </button>
          <a href={botInviteUrl()}>Add to Discord</a>
          <button
            type="button"
            onClick={() => {
              if (getStoredToken()) {
                window.location.href = "/servers";
              } else {
                void initiateLogin("/servers");
              }
            }}
          >
            Dashboard
          </button>
          <a
            href="https://discord.gg/xT9vgkTHqF"
            target="_blank"
            rel="noopener noreferrer"
          >
            Support server
          </a>
        </div>
        <div>
          <h4>Legal</h4>
          <button type="button" onClick={() => onNavigate("tos")}>
            Terms of Service
          </button>
          <button type="button" onClick={() => onNavigate("privacy-policy")}>
            Privacy Policy
          </button>
        </div>
      </nav>
    </footer>
  );
}

// ── Admin Panel ───────────────────────────────────────────────────────────────

type AdminSortKey = "name" | "messages" | "lore" | "members" | "installed";
type AdminSortDir = "asc" | "desc";

function AdminPage({
  me,
  onNavigate,
  onOpenServer,
}: {
  me: MeResponse | null;
  onNavigate: (page: PageId) => void;
  onOpenServer: (serverId: string) => void;
}) {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [servers, setServers] = useState<AdminServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<AdminSortKey>("messages");
  const [sortDir, setSortDir] = useState<AdminSortDir>("desc");

  useEffect(() => {
    setLoading(true);
    Promise.all([getAdminStats(), getAdminServers()])
      .then(([s, sv]) => {
        setStats(s);
        setServers(sv.servers);
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Failed to load admin data");
        setLoading(false);
      });
  }, []);

  function toggleSort(key: AdminSortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return servers.filter(
      (s) =>
        !q ||
        s.name.toLowerCase().includes(q) ||
        s.id.includes(q) ||
        s.tier.toLowerCase().includes(q),
    );
  }, [servers, query]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortKey === "name") cmp = a.name.localeCompare(b.name);
      else if (sortKey === "messages") cmp = a.message_count - b.message_count;
      else if (sortKey === "lore") cmp = a.lore_count - b.lore_count;
      else if (sortKey === "members") cmp = a.member_count - b.member_count;
      else if (sortKey === "installed")
        cmp = new Date(a.joined_at).getTime() - new Date(b.joined_at).getTime();
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortKey, sortDir]);

  function SortBtn({ col, label }: { col: AdminSortKey; label: string }) {
    const active = sortKey === col;
    return (
      <button
        type="button"
        className={`admin-th-btn${active ? " active" : ""}`}
        onClick={() => toggleSort(col)}
      >
        {label}
        <span className="admin-sort-icon" aria-hidden="true">
          {active ? (sortDir === "asc" ? " ↑" : " ↓") : " ↕"}
        </span>
      </button>
    );
  }

  return (
    <main className="marketing-shell">
      <MarketingNav me={me} onNavigate={onNavigate} />

      <div className="admin-shell">
        <div className="admin-header">
          <div>
            <p className="eyebrow">Internal</p>
            <h2>Kodama Control Center</h2>
          </div>
          <span className="status-pill status-good admin-badge">
            <ShieldCheck size={13} aria-hidden="true" />
            Admin access
          </span>
        </div>

        {loading && (
          <div className="admin-loading">
            <LoadingDots label="Loading admin data" />
          </div>
        )}

        {error && <p className="admin-error">{error}</p>}

        {!loading && stats && (
          <>
            <div className="admin-stats-grid">
              <article className="admin-stat-card">
                <p>Servers</p>
                <strong>{stats.totalServers.toLocaleString()}</strong>
                <span>All installed</span>
              </article>
              <article className="admin-stat-card">
                <p>Messages</p>
                <strong>{stats.totalMessages.toLocaleString()}</strong>
                <span>Indexed total</span>
              </article>
              <article className="admin-stat-card">
                <p>Lore entries</p>
                <strong>{stats.totalLore.toLocaleString()}</strong>
                <span>Pinned + detected</span>
              </article>
              <article className="admin-stat-card">
                <p>Members</p>
                <strong>{stats.totalMembers.toLocaleString()}</strong>
                <span>Tracked across all servers</span>
              </article>
              <article className="admin-stat-card admin-stat-warn">
                <p>Opted out</p>
                <strong>{stats.totalOptedOut.toLocaleString()}</strong>
                <span>Excluded from indexing</span>
              </article>
              <article className="admin-stat-card">
                <p>Active sessions</p>
                <strong>{stats.activeSessions.toLocaleString()}</strong>
                <span>Dashboard sessions in DB</span>
              </article>
            </div>

            <div className="admin-table-header">
              <label className="admin-search">
                <Search size={15} aria-hidden="true" />
                <input
                  placeholder="Filter by name, ID or tier…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Filter servers"
                />
              </label>
              <div className="admin-table-title">
                <ServerIcon size={16} aria-hidden="true" />
                <strong>All servers</strong>
                <span className="admin-count">{servers.length}</span>
              </div>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th className="admin-th-server">
                      <SortBtn col="name" label="Server" />
                    </th>
                    <th>Tier</th>
                    <th>
                      <SortBtn col="messages" label="Messages" />
                    </th>
                    <th>
                      <SortBtn col="lore" label="Lore" />
                    </th>
                    <th>
                      <SortBtn col="members" label="Members" />
                    </th>
                    <th>Opted out</th>
                    <th>Last indexed</th>
                    <th>
                      <SortBtn col="installed" label="Installed" />
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 && (
                    <tr>
                      <td colSpan={8} className="admin-empty-row">
                        No servers match
                      </td>
                    </tr>
                  )}
                  {sorted.map((server) => (
                    <tr key={server.id} className="admin-row">
                      <td className="admin-td-server">
                        <span className="admin-server-icon" aria-hidden="true">
                          {server.icon_url ? (
                            <img src={server.icon_url} alt="" />
                          ) : (
                            server.name.slice(0, 2).toUpperCase()
                          )}
                        </span>
                        <span className="admin-server-info">
                          <strong>{server.name}</strong>
                          <code className="admin-server-id">{server.id}</code>
                        </span>
                      </td>
                      <td>
                        <span
                          className={`status-pill ${
                            server.tier === "chronicle"
                              ? "status-good"
                              : server.tier === "index"
                                ? "admin-tier-index"
                                : "status-muted"
                          }`}
                        >
                          {server.tier}
                        </span>
                      </td>
                      <td className="admin-td-num">
                        {server.message_count.toLocaleString()}
                      </td>
                      <td className="admin-td-num">
                        {server.lore_count.toLocaleString()}
                      </td>
                      <td className="admin-td-num">
                        {server.member_count.toLocaleString()}
                      </td>
                      <td className="admin-td-num">
                        {server.opted_out_count > 0 ? (
                          <span className="admin-opted-out">
                            {server.opted_out_count}
                          </span>
                        ) : (
                          <span className="admin-td-zero">0</span>
                        )}
                      </td>
                      <td className="admin-td-date">
                        {server.last_indexed_at
                          ? formatDate(server.last_indexed_at)
                          : <span className="admin-td-zero">Never</span>}
                      </td>
                      <td className="admin-td-date">
                        {formatDate(server.joined_at)}
                      </td>
                      <td className="admin-td-action">
                        <button
                          type="button"
                          className="admin-open-btn"
                          onClick={() => onOpenServer(server.id)}
                          title={`Open ${server.name} dashboard`}
                        >
                          Open
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <MarketingFooter onNavigate={onNavigate} />
    </main>
  );
}

function LandingPage({
  me,
  onNavigate,
}: {
  me: MeResponse | null;
  onNavigate: (page: PageId) => void;
}) {
  return (
    <main className="marketing-shell">
      <MarketingNav me={me} onNavigate={onNavigate} />

      <section className="hero-new">
        <div className="hero-new-content">
          <div className="hero-tag">
            <span className="hero-tag-dot" aria-hidden="true" />
            Discord Community Bot
          </div>
          <h1 className="hero-new-headline">
            Your server's
            <br />
            <span className="hero-headline-accent">lore</span>
            <br />
            lives on.
          </h1>
          <p className="hero-new-body">
            Kodama learns the lore, slang, and rhythm of your community. Then
            it talks back like it&apos;s been there since day one.
          </p>
          <div className="hero-new-ctas">
            <a href={botInviteUrl()} className="primary-button">
              <PlusCircle size={18} aria-hidden="true" />
              Add to Discord — free
            </a>
            <button
              type="button"
              className="hero-cta-ghost"
              onClick={() => {
                if (getStoredToken()) {
                  window.location.href = "/servers";
                } else {
                  void initiateLogin("/servers");
                }
              }}
            >
              Open dashboard
              <ArrowRight size={16} aria-hidden="true" />
            </button>
          </div>
          <div className="hero-chips">
            <span>Lore search</span>
            <span>Daily recaps</span>
            <span>Personality modes</span>
            <span>Privacy controls</span>
            <span>Inside joke detector</span>
          </div>
        </div>
        <div className="hero-new-demo">
          <DiscordMockup />
        </div>
      </section>

      <section className="features-new" aria-label="Features">
        <div className="features-header">
          <p className="eyebrow">What Kodama does</p>
          <h2 className="features-headline">
            A living AI that remembers your server's lore, jokes, and the
            people who shaped them.
          </h2>
        </div>
        <div className="features-grid-new">
          <article className="feature-item">
            <div className="feature-item-icon feature-icon-mint">
              <BookOpen size={20} aria-hidden="true" />
            </div>
            <h3>Memory engine</h3>
            <p>
              Indexes every message, pins high-signal moments, and builds a
              searchable lore archive. No more &ldquo;wait who was that
              again.&rdquo;
            </p>
            <code>/lore search tournament winner</code>
          </article>
          <article className="feature-item">
            <div className="feature-item-icon feature-icon-cyan">
              <Sparkles size={20} aria-hidden="true" />
            </div>
            <h3>Server voice</h3>
            <p>
              Learns your server&apos;s slang, energy, and inside references.
              Replies in a way that sounds like it grew up in the server.
            </p>
            <code>/settings personality mirror</code>
          </article>
          <article className="feature-item">
            <div className="feature-item-icon feature-icon-violet">
              <ShieldCheck size={20} aria-hidden="true" />
            </div>
            <h3>Privacy first</h3>
            <p>
              Members can opt out or hard-delete specific messages. Deleted
              Discord messages disappear from Kodama too, automatically.
            </p>
            <code>/optout</code>
          </article>
          <article className="feature-item">
            <div className="feature-item-icon feature-icon-mint">
              <CalendarClock size={20} aria-hidden="true" />
            </div>
            <h3>Daily recaps</h3>
            <p>
              Auto-summarizes the day&apos;s conversations, major moments, and
              who was active. Posted on schedule to any channel you choose.
            </p>
            <code>/recap today</code>
          </article>
          <article className="feature-item">
            <div className="feature-item-icon feature-icon-cyan">
              <MessageCircle size={20} aria-hidden="true" />
            </div>
            <h3>Lives in chat</h3>
            <p>
              @mention Kodama anywhere and it pulls from server memory to
              answer. Organic mode lets it jump in unprompted when the moment
              fits.
            </p>
            <code>@Kodama what's ryder's deal</code>
          </article>
          <article className="feature-item">
            <div className="feature-item-icon feature-icon-violet">
              <SlidersHorizontal size={20} aria-hidden="true" />
            </div>
            <h3>Mod controls</h3>
            <p>
              Personality, flame mode, recap schedule, and every behavior toggle
              lives in a per-server dashboard. Mods stay in control.
            </p>
            <code>/settings flame on</code>
          </article>
        </div>
      </section>

      <section className="cta-new" aria-label="Get started">
        <div className="cta-new-inner">
          <img
            src="/kodama_logo.png"
            alt=""
            className="cta-mascot"
            aria-hidden="true"
          />
          <div className="cta-new-copy">
            <h2>Start building your server&apos;s memory.</h2>
            <p>
              Free to add. No credit card. Works in any server where you have
              Manage Server permission.
            </p>
          </div>
          <a href={botInviteUrl()} className="primary-button cta-new-btn">
            <PlusCircle size={18} aria-hidden="true" />
            Add Kodama to Discord
          </a>
        </div>
      </section>

      <MarketingFooter onNavigate={onNavigate} />
    </main>
  );
}

function InfoPage({
  me,
  onNavigate,
}: {
  me: MeResponse | null;
  onNavigate: (page: PageId) => void;
}) {
  const commands = [
    ["/lore search", "Find old jokes, decisions, moments, and references."],
    ["/lore add", "Pin a memory manually when mods want it saved."],
    ["/lore list", "Review recently saved lore."],
    ["/whois", "Summarize a member's server profile and notable moments."],
    ["/recap", "Generate a daily, weekly, or monthly server summary."],
    ["/forget", "Hard-delete a specific indexed message from memory."],
    ["/optout", "Exclude a member from indexing and summaries."],
    ["/settings", "Tune personality, flame mode, and recap delivery."],
  ];

  return (
    <main className="marketing-shell">
      <MarketingNav me={me} onNavigate={onNavigate} />

      <section className="info-shell">
        <div className="info-heading">
          <p className="eyebrow">Info</p>
          <h2>Status and commands.</h2>
        </div>

        <section className="status-grid" aria-label="Kodama status">
          <article>
            <CheckCircle2 size={20} aria-hidden="true" />
            <strong>Bot gateway</strong>
            <span>Online</span>
          </article>
          <article>
            <ShieldCheck size={20} aria-hidden="true" />
            <strong>Privacy actions</strong>
            <span>Forget and opt-out enabled</span>
          </article>
          <article>
            <CalendarClock size={20} aria-hidden="true" />
            <strong>Recaps</strong>
            <span>Scheduled per server</span>
          </article>
        </section>

        <section className="commands-table" aria-label="Kodama commands">
          {commands.map(([command, description]) => (
            <article key={command}>
              <code>{command}</code>
              <p>{description}</p>
            </article>
          ))}
        </section>
      </section>
      <MarketingFooter onNavigate={onNavigate} />
    </main>
  );
}

function TosPage({
  me,
  onNavigate,
}: {
  me: MeResponse | null;
  onNavigate: (page: PageId) => void;
}) {
  return (
    <main className="marketing-shell">
      <MarketingNav me={me} onNavigate={onNavigate} />
      <div className="legal-shell">
        <div className="legal-header">
          <p className="eyebrow">Legal</p>
          <h1>Terms of Service</h1>
          <p>Effective date: May 26, 2026</p>
        </div>
        <div className="legal-body">
          <section className="legal-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By adding Kodama to your Discord server or using Kodama&apos;s
              dashboard, you agree to these Terms of Service. If you do not
              agree, do not use the service.
            </p>
          </section>
          <section className="legal-section">
            <h2>2. Description of Service</h2>
            <p>
              Kodama is a Discord bot that indexes server messages, detects
              lore moments and inside jokes, generates daily recaps, builds
              member profiles, and provides personality-driven replies based on
              server memory. A web dashboard is provided for server
              administrators to manage settings and view server data.
            </p>
          </section>
          <section className="legal-section">
            <h2>3. Eligibility</h2>
            <ul>
              <li>
                You must comply with Discord&apos;s{" "}
                <a
                  href="https://discord.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </a>
                .
              </li>
              <li>
                You must have Manage Server or Administrator permission in any
                Discord server where you install Kodama.
              </li>
              <li>
                You must be at least 13 years old (Discord&apos;s minimum age
                requirement).
              </li>
            </ul>
          </section>
          <section className="legal-section">
            <h2>4. Acceptable Use</h2>
            <p>You may not use Kodama to:</p>
            <ul>
              <li>
                Collect or distribute information about users for any unlawful
                purpose.
              </li>
              <li>
                Harass, harm, threaten, or discriminate against any person.
              </li>
              <li>
                Circumvent or interfere with Kodama&apos;s privacy controls,
                including opt-out and forget commands.
              </li>
              <li>
                Reverse-engineer, scrape, or abuse the service in ways that
                degrade availability for others.
              </li>
              <li>Use the service for any illegal purpose.</li>
            </ul>
          </section>
          <section className="legal-section">
            <h2>5. Privacy Controls</h2>
            <p>
              Kodama provides the following privacy controls that are always
              available to all server members, regardless of tier:
            </p>
            <ul>
              <li>
                <strong>/optout</strong> — Exclude yourself from all future
                indexing and summaries. Existing indexed data from you will be
                removed.
              </li>
              <li>
                <strong>/optin</strong> — Re-enable indexing after opting out.
              </li>
              <li>
                <strong>/forget &lt;message_link&gt;</strong> — Permanently
                hard-delete a specific message from Kodama&apos;s memory.
              </li>
              <li>
                <strong>Automatic deletion</strong> — When you delete a message
                in Discord, Kodama removes it from its index within 5 minutes.
              </li>
            </ul>
          </section>
          <section className="legal-section">
            <h2>6. Data Collection and Processing</h2>
            <p>
              See our{" "}
              <button
                type="button"
                className="legal-inline-link"
                onClick={() => onNavigate("privacy-policy")}
              >
                Privacy Policy
              </button>{" "}
              for full details on what data Kodama collects and how it is
              processed.
            </p>
          </section>
          <section className="legal-section">
            <h2>7. Service Availability</h2>
            <p>
              Kodama is provided &ldquo;as is&rdquo; without warranties of any
              kind. We do not guarantee uptime, accuracy of lore, or
              uninterrupted availability. We may modify or discontinue the
              service at any time with reasonable notice.
            </p>
          </section>
          <section className="legal-section">
            <h2>8. Intellectual Property</h2>
            <p>
              The Kodama service, including all code, branding, and design, is
              owned by the Kodama team. Server data processed through Kodama
              remains the property of the respective Discord server and its
              members. You grant Kodama a limited license to process that data
              solely for the purpose of operating the service.
            </p>
          </section>
          <section className="legal-section">
            <h2>9. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by applicable law, Kodama and its
              operators shall not be liable for any indirect, incidental,
              special, consequential, or punitive damages arising from your use
              of the service, including loss of data, server disruption, or
              unauthorized access.
            </p>
          </section>
          <section className="legal-section">
            <h2>10. Termination</h2>
            <p>
              You may remove Kodama from your server at any time, which
              terminates data collection for that server going forward. We may
              suspend or terminate access for violations of these terms. Upon
              request following removal, we will delete server data.
            </p>
          </section>
          <section className="legal-section">
            <h2>11. Changes to These Terms</h2>
            <p>
              We may update these terms from time to time. Continued use of the
              service after changes are posted constitutes acceptance of the
              updated terms. Material changes will be announced in our support
              channels.
            </p>
          </section>
          <section className="legal-section">
            <h2>12. Contact</h2>
            <p>
              For questions about these terms, reach out via our{" "}
              <a
                href="https://discord.gg/xT9vgkTHqF"
                target="_blank"
                rel="noopener noreferrer"
              >
                Discord support server
              </a>
              .
            </p>
          </section>
        </div>
      </div>
      <MarketingFooter onNavigate={onNavigate} />
    </main>
  );
}

function PrivacyPolicyPage({
  me,
  onNavigate,
}: {
  me: MeResponse | null;
  onNavigate: (page: PageId) => void;
}) {
  return (
    <main className="marketing-shell">
      <MarketingNav me={me} onNavigate={onNavigate} />
      <div className="legal-shell">
        <div className="legal-header">
          <p className="eyebrow">Legal</p>
          <h1>Privacy Policy</h1>
          <p>Effective date: May 26, 2026</p>
        </div>
        <div className="legal-body">
          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              This Privacy Policy explains how Kodama collects, uses, and
              protects information from Discord servers where it is installed.
              We take privacy seriously and have built user-controlled deletion
              and opt-out tools into the core product.
            </p>
          </section>
          <section className="legal-section">
            <h2>2. What We Collect</h2>
            <ul>
              <li>
                <strong>Message content</strong> — Text content of messages in
                channels where Kodama is active, used to build server memory
                and respond in context. Audio and images are not collected.
              </li>
              <li>
                <strong>Message metadata</strong> — Channel IDs, timestamps,
                reaction counts, and Discord user IDs associated with each
                message.
              </li>
              <li>
                <strong>Server information</strong> — Discord server (guild) ID,
                name, and channel configuration chosen by server admins.
              </li>
              <li>
                <strong>Dashboard accounts</strong> — If you sign in to the
                Kodama dashboard via Discord OAuth, we store your Discord user
                ID and a short-lived session token. We do not store passwords.
              </li>
            </ul>
          </section>
          <section className="legal-section">
            <h2>3. What We Do Not Collect</h2>
            <ul>
              <li>Discord email addresses</li>
              <li>Direct messages (DMs)</li>
              <li>Voice channel audio or video</li>
              <li>
                Messages in channels where Kodama has not been given access
              </li>
              <li>Payment information</li>
            </ul>
          </section>
          <section className="legal-section">
            <h2>4. How We Use Your Data</h2>
            <ul>
              <li>
                Generating semantic embeddings to power lore search and
                contextual replies
              </li>
              <li>
                Building server lore archives, inside-joke detection, and
                member profiles
              </li>
              <li>Generating daily recaps and personality-aware replies</li>
              <li>
                Providing privacy audit logs so server admins can see what data
                actions have occurred
              </li>
            </ul>
            <p>
              We do not sell your data, use it for advertising, or share it
              with third parties beyond the processing services described below.
            </p>
          </section>
          <section className="legal-section">
            <h2>5. Third-Party Services</h2>
            <p>
              Kodama uses the following third-party services to operate. Message
              content may be transmitted to these services for processing:
            </p>
            <ul>
              <li>
                <strong>Anthropic (Claude API)</strong> — For generating replies,
                lore summaries, recaps, and member profiles. Anthropic&apos;s
                usage policies apply.
              </li>
              <li>
                <strong>OpenAI (Embeddings API)</strong> — For generating vector
                embeddings used in semantic lore search. OpenAI&apos;s usage
                policies apply.
              </li>
              <li>
                <strong>Discord API</strong> — For reading server messages and
                posting bot responses. Discord&apos;s Privacy Policy applies.
              </li>
            </ul>
          </section>
          <section className="legal-section">
            <h2>6. Your Privacy Controls</h2>
            <p>
              Every member of a Kodama-installed server has access to these
              controls at all times:
            </p>
            <ul>
              <li>
                <strong>/optout</strong> — Removes you from all future indexing
                and deletes your existing indexed messages. Kodama will not
                mention you in summaries or profiles.
              </li>
              <li>
                <strong>/optin</strong> — Re-enables indexing after opting out.
              </li>
              <li>
                <strong>/forget &lt;message_link&gt;</strong> — Permanently
                deletes a specific message from Kodama&apos;s memory. This
                action is logged in the server&apos;s audit trail.
              </li>
              <li>
                <strong>Automatic deletion</strong> — When you delete a message
                in Discord, Kodama removes it from its memory index within 5
                minutes.
              </li>
            </ul>
          </section>
          <section className="legal-section">
            <h2>7. Data Retention</h2>
            <ul>
              <li>
                Server message data is retained for as long as Kodama is
                installed in the server.
              </li>
              <li>
                When Kodama is removed from a server, data for that server can
                be deleted upon written request to us.
              </li>
              <li>
                Data from opted-out members is deleted and excluded from all
                future indexing immediately upon use of /optout.
              </li>
              <li>
                Dashboard session tokens expire automatically and are not
                stored long-term.
              </li>
            </ul>
          </section>
          <section className="legal-section">
            <h2>8. Data Security</h2>
            <p>
              Server data is stored in a self-hosted database on private
              infrastructure, accessible only to Kodama&apos;s servers. The
              database is not exposed to the public internet. We use encryption
              in transit (HTTPS/TLS) for all API communication.
            </p>
          </section>
          <section className="legal-section">
            <h2>9. Children&apos;s Privacy</h2>
            <p>
              Kodama does not knowingly collect personal data from users under
              13 years old. Discord&apos;s own minimum age requirement applies.
              If you believe data from a minor has been collected, contact us
              immediately and we will remove it.
            </p>
          </section>
          <section className="legal-section">
            <h2>10. Changes to This Policy</h2>
            <p>
              We may update this policy periodically. The effective date at the
              top of this page will reflect the most recent revision.
              Significant changes will be announced in our support channels.
            </p>
          </section>
          <section className="legal-section">
            <h2>11. Contact and Data Requests</h2>
            <p>
              For privacy questions, data export requests, or deletion requests,
              reach out via our Discord support server. We will respond within
              30 days.
            </p>
          </section>
        </div>
      </div>
      <MarketingFooter onNavigate={onNavigate} />
    </main>
  );
}

function ServerManagementPage({
  me,
  loading,
  onNavigate,
  onManageServer,
}: {
  me: MeResponse | null;
  loading: boolean;
  onNavigate: (page: PageId) => void;
  onManageServer: (serverId: string) => void;
}) {
  const servers = me?.servers ?? [];
  const addableGuilds = me?.addableGuilds ?? [];
  const hasGuildChoices = servers.length > 0 || addableGuilds.length > 0;

  return (
    <main className="marketing-shell">
      <MarketingNav me={me} onNavigate={onNavigate} />

      <section className="servers-header">
        <div>
          <p className="eyebrow">Servers</p>
          <h2>Manage Kodama.</h2>
        </div>
        <a className="secondary-button add-bot-button" href={botInviteUrl()}>
          <PlusCircle size={18} aria-hidden="true" />
          Add Kodama
        </a>
      </section>

      {loading && servers.length === 0 ? (
        <EmptyState
          icon={<LoadingDots label="Loading servers" />}
          title="Loading servers"
          body="Checking which Discord servers you can manage."
        />
      ) : !hasGuildChoices ? (
        <EmptyState
          icon={<ServerIcon size={24} aria-hidden="true" />}
          title="No manageable servers"
          body="Discord did not return any servers where you have Manage Server or Administrator permission."
          action={
            <a className="primary-button" href={botInviteUrl()}>
              <PlusCircle size={18} aria-hidden="true" />
              Add Kodama
            </a>
          }
        />
      ) : (
        <section className="server-picker-panel" aria-label="Manage servers">
          <div className="server-picker-bar">
            <ServerIcon size={18} aria-hidden="true" />
            Server picker
          </div>
          <div className="server-picker-body">
            <h3>Select a server to configure</h3>
            <div className="server-orbit">
              {servers.map((server) => (
                <button
                  className="server-bubble server-bubble-installed"
                  key={server.id}
                  type="button"
                  onClick={() => onManageServer(server.id)}
                >
                  <span aria-hidden="true">
                    {server.icon_url ? (
                      <img src={server.icon_url} alt="" aria-hidden="true" />
                    ) : (
                      server.name.slice(0, 2).toUpperCase()
                    )}
                  </span>
                  <strong>{server.name}</strong>
                  <small>
                    {server.has_recap ? "Ready" : "Needs setup"} ·{" "}
                    {server.personality}
                  </small>
                </button>
              ))}
              {addableGuilds.map((guild) => (
                <ServerInviteBubble guild={guild} key={guild.id} />
              ))}
            </div>
          </div>
          <a className="server-picker-add" href={botInviteUrl()}>
            <PlusCircle size={17} aria-hidden="true" />
            Add another server
          </a>
        </section>
      )}
    </main>
  );
}

function ServerInviteBubble({ guild }: { guild: ManageableGuild }) {
  return (
    <a
      className="server-bubble server-bubble-add"
      href={botInviteUrl(guild.id)}
    >
      <span aria-hidden="true">
        {guild.icon_url ? (
          <img src={guild.icon_url} alt="" aria-hidden="true" />
        ) : (
          <PlusCircle size={34} aria-hidden="true" />
        )}
      </span>
      <strong>{guild.name}</strong>
      <small>Add Kodama</small>
    </a>
  );
}

function OverviewPanel({
  detail,
  onNavigate,
  saving,
  scanRunning,
  onScan,
  onUpgrade,
}: {
  detail: ServerDetail;
  onNavigate: (tab: TabId) => void;
  onSavePatch: (patch: SettingsPatch) => Promise<void>;
  saving: boolean;
  scanRunning: boolean;
  onScan: () => void;
  onUpgrade: () => void;
}) {
  const { server, overview } = detail;
  const voiceReady = Boolean(server.voice_profile);
  const recapsReady = Boolean(server.recap_channel_id && server.recap_time);

  const voiceTraits = [
    {
      label: "Mode",
      value: server.personality,
      detail:
        server.personality === "mirror"
          ? "Matches the server's own rhythm"
          : "Uses a fixed personality preset",
    },
    {
      label: "Emoji bank",
      value:
        server.emoji_bank.length > 0 ? server.emoji_bank.join(" ") : "Not set",
      detail: "Drawn from recent server usage",
    },
    {
      label: "Roast behavior",
      value: server.allow_roasting ? "Enabled" : "Disabled",
      detail: "Controlled in Controls",
    },
  ];

  return (
    <div className="tab-panel">
      <section className="summary-grid" aria-label="Overview">
        <StatCard
          label="Message base"
          value={overview.messageCount.toLocaleString()}
          detail={`Last indexed ${formatDate(overview.lastIndexedAt)}`}
          tone="good"
        />
        <StatCard
          label="Memory shelf"
          value={overview.loreCount.toLocaleString()}
          detail="Pinned lore and detected moments"
          tone="good"
        />
        <StatCard
          label="Daily recap"
          value={recapsReady ? "On" : "Off"}
          detail={
            recapsReady
              ? `${server.recap_time} UTC`
              : "Choose a channel and time"
          }
          tone={recapsReady ? "good" : "warn"}
        />
        <StatCard
          label="Guardrail log"
          value={overview.auditCountToday.toLocaleString()}
          detail={`${overview.optedOutCount} members opted out`}
        />
      </section>

      <section className="work-area">
        <div className="panel main-panel">
          <SectionHeader
            eyebrow="Workspace"
            title={`${server.name} ${voiceReady ? "has a working voice model" : "is ready for its first read"}`}
            aside={
              <StatusPill>
                {voiceReady ? "Voice active" : "Learning"}
              </StatusPill>
            }
          />

          <div className="health-grid">
            <div className="health-card">
              <CheckCircle2 size={18} aria-hidden="true" />
              <div>
                <strong>Memory index</strong>
                <span>
                  {overview.messageCount > 0
                    ? `${overview.messageCount.toLocaleString()} messages available for lore and context`
                    : "Waiting for server messages"}
                </span>
              </div>
            </div>
            <div className="health-card">
              <Sparkles size={18} aria-hidden="true" />
              <div>
                <strong>Server voice</strong>
                <span>
                  {server.voice_profile ??
                    "Kodama will build a style guide from recent messages."}
                </span>
              </div>
            </div>
            <div className="health-card">
              <ShieldCheck size={18} aria-hidden="true" />
              <div>
                <strong>Privacy</strong>
                <span>Opt-out, forget, and message deletion are tracked.</span>
              </div>
            </div>
          </div>

          <div className="quick-grid">
            <QuickAction
              icon={<Search size={18} aria-hidden="true" />}
              label="Memory"
              detail="Browse saved lore and detected moments"
              onClick={() => onNavigate("memory")}
            />
            <QuickAction
              icon={<SlidersHorizontal size={18} aria-hidden="true" />}
              label="Controls"
              detail="Tune personality and flame mode"
              onClick={() => onNavigate("settings")}
            />
            <QuickAction
              icon={<CalendarClock size={18} aria-hidden="true" />}
              label="Schedule"
              detail="Set recap channel and time"
              onClick={() => onNavigate("recaps")}
            />
          </div>
        </div>

        <aside className="panel side-panel">
          <SectionHeader
            eyebrow="Controls"
            title="Live settings"
            aside={<StatusPill tone={saving ? "warn" : "good"}>Live</StatusPill>}
          />
          <div className="setup-list">
            <button type="button" className="setup-row-btn" onClick={() => onNavigate("settings")}>
              <span>Personality</span>
              <strong>{server.personality}</strong>
            </button>
            <button type="button" className="setup-row-btn" onClick={() => onNavigate("settings")}>
              <span>Reply frequency</span>
              <strong>
                {server.mention_behavior === "silent"
                  ? "@mentions only"
                  : server.mention_behavior === "chatty"
                    ? "Active"
                    : "Conversational"}
              </strong>
            </button>
            <button type="button" className="setup-row-btn" onClick={() => onNavigate("recaps")}>
              <span>Recap channel</span>
              <strong>{server.recap_channel_id ?? "Not set"}</strong>
            </button>
            <button type="button" className="setup-row-btn" onClick={() => onNavigate("recaps")}>
              <span>Recap time</span>
              <strong>{server.recap_time ? `${server.recap_time} UTC` : "Not set"}</strong>
            </button>
            <button type="button" className="setup-row-btn" onClick={() => onNavigate("settings")}>
              <span>Emoji bank</span>
              <strong>
                {server.emoji_bank.length > 0 ? server.emoji_bank.join(" ") : "Not set"}
              </strong>
            </button>
          </div>
          <div className={`setup-scan-row${scanRunning ? " scanning" : ""}`}>
            {meetsRequiredTier(detail.server.tier, "index") ? (
              <button
                type="button"
                className="secondary-button"
                disabled={scanRunning}
                onClick={onScan}
              >
                {scanRunning ? (
                  <LoadingDots label="Scanning history" />
                ) : (
                  <>
                    <History size={15} aria-hidden="true" />
                    Server scan
                  </>
                )}
              </button>
            ) : (
              <div className="locked-btn-wrap" onClick={onUpgrade} role="button" tabIndex={0}>
                <button type="button" className="secondary-button" tabIndex={-1} disabled>
                  <History size={15} aria-hidden="true" />
                  Server scan
                </button>
                <span className="locked-btn-icon" aria-label="Upgrade to Index to unlock">
                  <LockKeyhole size={13} aria-hidden="true" />
                </span>
              </div>
            )}
            {meetsRequiredTier(detail.server.tier, "chronicle") ? (
              <button
                type="button"
                className="secondary-button"
                disabled={scanRunning}
                onClick={onScan}
              >
                {scanRunning ? (
                  <LoadingDots label="Scanning history" />
                ) : (
                  <>
                    <History size={15} aria-hidden="true" />
                    Deep scan
                  </>
                )}
              </button>
            ) : (
              <div className="locked-btn-wrap" onClick={onUpgrade} role="button" tabIndex={0}>
                <button type="button" className="secondary-button" tabIndex={-1} disabled>
                  <History size={15} aria-hidden="true" />
                  Deep scan
                </button>
                <span className="locked-btn-icon" aria-label="Upgrade to Chronicle to unlock">
                  <LockKeyhole size={13} aria-hidden="true" />
                </span>
              </div>
            )}
          </div>
        </aside>
      </section>

      <section className="panel voice-shape-panel" aria-label="Voice shape">
        <SectionHeader
          eyebrow="Voice"
          title="Voice shape"
          aside={<Sparkles size={18} aria-hidden="true" />}
        />
        <p className="voice-shape-summary">
          {server.voice_profile ??
            "Kodama has not learned enough recent messages to summarize this server's style yet."}
        </p>
        <div className="feature-grid overview-voice-grid">
          {voiceTraits.map((trait) => (
            <article className="feature-card" key={trait.label}>
              <span>{trait.label}</span>
              <strong>{trait.value}</strong>
              <p>{trait.detail}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function reportTypeLabel(type: string): string {
  if (type === "inside_joke" || type === "lore") return "Lore";
  if (type === "recap") return "Daily recap";
  return "Update";
}

function MemoryPanel({
  reports,
  lore,
  working,
  error,
  tier,
  onAsk,
  onCreate,
  onDelete,
  onDismissReport,
  onPromoteReport,
  onUpgrade,
  scanRunning,
  onScan,
}: {
  reports: ServerReport[];
  lore: LoreEntry[];
  working: boolean;
  error: string | null;
  tier: string;
  onAsk: () => void;
  onCreate: (entry: { title: string; summary: string }) => Promise<void>;
  onDelete: (entry: LoreEntry) => Promise<void>;
  onDismissReport: (report: ServerReport) => Promise<void>;
  onPromoteReport: (report: ServerReport) => Promise<void>;
  onUpgrade: () => void;
  scanRunning: boolean;
  onScan: () => void;
}) {
  const [view, setView] = useState<"reports" | "archive">("reports");
  const [query, setQuery] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<LoreEntry | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [actingReportId, setActingReportId] = useState<string | null>(null);

  const filteredLore = useMemo(() => {
    const clean = query.trim().toLowerCase();
    if (!clean) return lore;
    return lore.filter((entry) =>
      `${entry.title} ${entry.summary} ${entry.category ?? ""}`
        .toLowerCase()
        .includes(clean),
    );
  }, [lore, query]);

  async function submitMemory(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextTitle = title.trim();
    const nextSummary = summary.trim();
    if (!nextTitle || !nextSummary) {
      setFormError("Add a title and memory details.");
      return;
    }
    setFormError(null);
    try {
      await onCreate({ title: nextTitle, summary: nextSummary });
      setTitle("");
      setSummary("");
      setAddOpen(false);
    } catch {
      // The parent owns the user-facing API error.
    }
  }

  async function doDeleteMemory(entry: LoreEntry): Promise<void> {
    setConfirmDelete(null);
    setDeletingId(entry.id);
    try {
      await onDelete(entry);
    } catch {
      // The parent owns the user-facing API error.
    } finally {
      setDeletingId(null);
    }
  }

  async function dismissOne(report: ServerReport): Promise<void> {
    setActingReportId(report.id);
    try {
      await onDismissReport(report);
    } finally {
      setActingReportId(null);
    }
  }

  async function promoteOne(report: ServerReport): Promise<void> {
    setActingReportId(report.id);
    try {
      await onPromoteReport(report);
    } finally {
      setActingReportId(null);
    }
  }

  return (
    <>
      <section className="panel page-panel tab-panel">
        <SectionHeader
          eyebrow="Server memory"
          title="Memory"
          aside={
            reports.length > 0 ? (
              <StatusPill tone="good">
                <Bell size={12} aria-hidden="true" />
                {reports.length} new
              </StatusPill>
            ) : (
              <StatusPill tone="muted">{lore.length} pinned</StatusPill>
            )
          }
        />

        <div className="memory-view-tabs">
          <button
            type="button"
            className={`memory-view-tab${view === "reports" ? " active" : ""}`}
            onClick={() => setView("reports")}
          >
            <Bell size={14} aria-hidden="true" />
            Reports
            {reports.length > 0 && (
              <span className="memory-tab-badge">{reports.length}</span>
            )}
          </button>
          <button
            type="button"
            className={`memory-view-tab${view === "archive" ? " active" : ""}`}
            onClick={() => setView("archive")}
          >
            <BookOpen size={14} aria-hidden="true" />
            Archive
          </button>
        </div>

        <div className="memory-toolbar">
          <button
            type="button"
            className="secondary-button"
            onClick={() => setAddOpen(true)}
          >
            <Pin size={16} aria-hidden="true" />
            Pin memory
          </button>
          {meetsRequiredTier(tier, "chronicle") ? (
            <button type="button" className="secondary-button" onClick={onAsk}>
              <Search size={16} aria-hidden="true" />
              Ask Kodama
            </button>
          ) : (
            <button type="button" className="secondary-button" onClick={onUpgrade}>
              <LockKeyhole size={16} aria-hidden="true" />
              Ask Kodama
            </button>
          )}
          <div className={`setup-scan-row${scanRunning ? " scanning" : ""}`}>
            {meetsRequiredTier(tier, "index") ? (
              <button
                type="button"
                className="secondary-button"
                disabled={scanRunning}
                onClick={onScan}
              >
                {scanRunning ? (
                  <LoadingDots label="Scanning history" />
                ) : (
                  <>
                    <History size={16} aria-hidden="true" />
                    Server scan
                  </>
                )}
              </button>
            ) : (
              <div className="locked-btn-wrap" onClick={onUpgrade} role="button" tabIndex={0}>
                <button type="button" className="secondary-button" tabIndex={-1} disabled>
                  <History size={16} aria-hidden="true" />
                  Server scan
                </button>
                <span className="locked-btn-icon" aria-label="Upgrade to Index to unlock">
                  <LockKeyhole size={13} aria-hidden="true" />
                </span>
              </div>
            )}
            {meetsRequiredTier(tier, "chronicle") ? (
              <button
                type="button"
                className="secondary-button"
                disabled={scanRunning}
                onClick={onScan}
              >
                {scanRunning ? (
                  <LoadingDots label="Scanning history" />
                ) : (
                  <>
                    <History size={16} aria-hidden="true" />
                    Deep scan
                  </>
                )}
              </button>
            ) : (
              <div className="locked-btn-wrap" onClick={onUpgrade} role="button" tabIndex={0}>
                <button type="button" className="secondary-button" tabIndex={-1} disabled>
                  <History size={16} aria-hidden="true" />
                  Deep scan
                </button>
                <span className="locked-btn-icon" aria-label="Upgrade to Chronicle to unlock">
                  <LockKeyhole size={13} aria-hidden="true" />
                </span>
              </div>
            )}
          </div>
        </div>

        {error && <p className="memory-error">{error}</p>}

        {view === "reports" && (
          <div className="memory-list">
            {reports.length === 0 ? (
              <EmptyState
                icon={<Bell size={24} aria-hidden="true" />}
                title="No new reports"
                body="Kodama will surface server lore here — memorable moments, running jokes, notable events, and daily recaps."
              />
            ) : (
              reports.map((report) => (
                <article className="report-item" key={report.id}>
                  <div className="report-header">
                    <StatusPill tone={report.type === "recap" ? "muted" : "good"}>
                      {reportTypeLabel(report.type)}
                    </StatusPill>
                    <span className="report-date">{formatDate(report.created_at)}</span>
                  </div>
                  <h4 className="report-headline">{report.headline}</h4>
                  <p className="report-body">{report.body}</p>
                  <div className="report-actions">
                    {report.type !== "recap" && (
                      <button
                        type="button"
                        className="secondary-button report-action-btn"
                        disabled={working || actingReportId === report.id}
                        onClick={() => void promoteOne(report)}
                      >
                        <BookmarkCheck size={14} aria-hidden="true" />
                        Pin to lore
                      </button>
                    )}
                    <button
                      type="button"
                      className="icon-button memory-delete"
                      title="Dismiss report"
                      aria-label={`Dismiss: ${report.headline}`}
                      disabled={working || actingReportId === report.id}
                      onClick={() => void dismissOne(report)}
                    >
                      <X size={15} aria-hidden="true" />
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>
        )}

        {view === "archive" && (
          <>
            <label className="search-row">
              <Search size={18} aria-hidden="true" />
              <input
                aria-label="Search saved memory"
                placeholder="Search jokes, events, people, or phrases"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
            <div className="memory-list">
              {filteredLore.length === 0 ? (
                <EmptyState
                  icon={<Search size={24} aria-hidden="true" />}
                  title={lore.length === 0 ? "No pinned memories yet" : "No matches"}
                  body={
                    lore.length === 0
                      ? "Pin memories manually or promote a report to build your archive."
                      : "Try a different phrase or clear the search."
                  }
                />
              ) : (
                filteredLore.map((entry) => (
                  <article className="memory-item" key={entry.id}>
                    <div className="memory-icon">
                      {entry.pinned ? (
                        <Pin size={16} aria-hidden="true" />
                      ) : (
                        <MessageCircle size={16} aria-hidden="true" />
                      )}
                    </div>
                    <div>
                      <div className="memory-title-row">
                        <h4>{entry.title}</h4>
                        <div className="memory-actions">
                          <StatusPill tone={entry.pinned ? "good" : "muted"}>
                            {entry.pinned
                              ? "Pinned"
                              : formatCategory(entry.category)}
                          </StatusPill>
                          <button
                            className="icon-button memory-delete"
                            type="button"
                            title="Delete memory"
                            aria-label={`Delete ${entry.title}`}
                            disabled={working || deletingId === entry.id}
                            onClick={() => setConfirmDelete(entry)}
                          >
                            <Trash2 size={15} aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                      <p>{entry.summary}</p>
                      <span>{formatDate(entry.created_at)}</span>
                    </div>
                  </article>
                ))
              )}
            </div>
          </>
        )}
      </section>

      {addOpen && (
        <div className="modal-scrim" role="presentation">
          <form className="memory-modal" onSubmit={submitMemory}>
            <SectionHeader
              eyebrow="Manual memory"
              title="Pin memory"
              aside={<StatusPill tone="muted">Pinned</StatusPill>}
            />
            <label>
              <span className="field-label">Title</span>
              <input
                maxLength={120}
                placeholder="S1 tournament winner"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
              />
            </label>
            <label>
              <span className="field-label">Details</span>
              <textarea
                maxLength={1200}
                placeholder="Ryder won the first server tournament after..."
                value={summary}
                onChange={(event) => setSummary(event.target.value)}
              />
            </label>
            {(formError || error) && (
              <small className="save-error">{formError ?? error}</small>
            )}
            <div className="modal-actions">
              <button
                className="secondary-button"
                type="button"
                disabled={working}
                onClick={() => setAddOpen(false)}
              >
                Cancel
              </button>
              <button
                className="primary-button"
                type="submit"
                disabled={working}
              >
                <Pin size={17} aria-hidden="true" />
                {working ? (
                  <LoadingDots label="Saving memory" tone="dark" />
                ) : (
                  "Save memory"
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {confirmDelete && (
        <ConfirmModal
          message={`Delete "${confirmDelete.title}" from Kodama's memory?`}
          confirmLabel="Delete"
          onConfirm={() => void doDeleteMemory(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </>
  );
}

function TypewriterText({ text }: { text: string }) {
  const words = text.split(" ");
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    setVisibleCount(0);
    if (words.length === 0) return;
    let i = 0;
    // Start fast then settle — front-loaded acceleration like real token streaming
    function scheduleNext() {
      if (i >= words.length) return;
      const delay = i < 4 ? 18 : i < 12 ? 28 : 38;
      const timer = window.setTimeout(() => {
        i++;
        setVisibleCount(i);
        scheduleNext();
      }, delay);
      return timer;
    }
    const first = scheduleNext();
    return () => window.clearTimeout(first);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <span>
      {words.slice(0, visibleCount).join(" ")}
      {visibleCount < words.length && (
        <span className="typewriter-cursor" aria-hidden="true" />
      )}
    </span>
  );
}

function AskKodamaWidget({
  open,
  serverName,
  messages,
  sending,
  error,
  onOpen,
  onClose,
  onSend,
}: {
  open: boolean;
  serverName: string;
  messages: AskMessage[];
  sending: boolean;
  error: string | null;
  onOpen: () => void;
  onClose: () => void;
  onSend: (question: string) => Promise<void>;
}) {
  const [draft, setDraft] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    window.setTimeout(() => inputRef.current?.focus(), 80);
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, open, sending]);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const question = draft.trim();
    if (!question || sending) return;
    setDraft("");
    await onSend(question);
  }

  return (
    <div className={`ask-widget ${open ? "ask-widget-open" : ""}`}>
      {open ? (
        <section className="ask-panel" aria-label="Ask Kodama">
          <header className="ask-header">
            <span className="ask-avatar" aria-hidden="true">
              <img src="/kodama_logo.png" alt="" />
            </span>
            <div>
              <p className="eyebrow">Ask Kodama</p>
              <h3>{serverName}</h3>
            </div>
            <button
              className="icon-button ask-close"
              type="button"
              aria-label="Close Ask Kodama"
              onClick={onClose}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </header>

          <div ref={listRef} className="ask-messages">
            {messages.length === 0 ? (
              <div className="ask-empty">
                <MessageCircle size={22} aria-hidden="true" />
                <strong>Ask about this server</strong>
                <span>Try a person, joke, event, phrase, or saved memory.</span>
              </div>
            ) : (
              messages.map((message, index) => (
                <div
                  className={`ask-bubble ask-${message.role}`}
                  key={`${message.role}-${index}`}
                >
                  {message.role === "kodama" && index === messages.length - 1 && !sending ? (
                    <TypewriterText text={message.content} />
                  ) : (
                    message.content
                  )}
                </div>
              ))
            )}
            {sending && (
              <div className="ask-bubble ask-kodama ask-loading">
                <LoadingDots />
              </div>
            )}
          </div>

          {error && <p className="ask-error">{error}</p>}

          <form className="ask-form" onSubmit={submit}>
            <input
              ref={inputRef}
              aria-label="Ask Kodama a question"
              maxLength={500}
              placeholder="Ask about this server..."
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
            <button
              className="primary-button ask-send"
              type="submit"
              disabled={sending || draft.trim().length === 0}
              aria-label="Send question"
            >
              <Send size={17} aria-hidden="true" />
            </button>
          </form>
        </section>
      ) : (
        <button
          className="ask-launcher"
          type="button"
          aria-label="Open Ask Kodama"
          onClick={onOpen}
        >
          <MessageCircle size={22} aria-hidden="true" />
          <span>Ask Kodama</span>
        </button>
      )}
    </div>
  );
}

function LoadingDots({
  label = "Loading",
  tone = "mint",
}: {
  label?: string;
  tone?: "mint" | "dark";
}) {
  return (
    <span className={`loading-dots loading-dots-${tone}`} aria-label={label}>
      <span />
      <span />
      <span />
    </span>
  );
}

function RecapsPanel({
  server,
  saving,
  saveError,
  onSave,
}: {
  server: ServerSummary;
  saving: boolean;
  saveError: string | null;
  onSave: (server: ServerSummary) => void;
}) {
  const ready = Boolean(server.recap_channel_id && server.recap_time);
  const [recapTime, setRecapTime] = useState(server.recap_time ?? "");
  const [recapChannel, setRecapChannel] = useState(server.recap_channel_id ?? "");

  useEffect(() => {
    setRecapTime(server.recap_time ?? "");
    setRecapChannel(server.recap_channel_id ?? "");
  }, [server]);

  const hasChanges =
    recapTime !== (server.recap_time ?? "") ||
    recapChannel !== (server.recap_channel_id ?? "");

  return (
    <section className="panel page-panel tab-panel">
      <SectionHeader
        eyebrow="Daily recaps"
        title="Schedule"
        aside={
          <StatusPill tone={ready ? "good" : "warn"}>
            {ready ? "Active" : "Needs setup"}
          </StatusPill>
        }
      />

      <div className="recap-board">
        <div className="recap-main">
          <Clock3 size={26} aria-hidden="true" />
          <span>Daily recap time</span>
          <strong>
            {server.recap_time ? `${server.recap_time} UTC` : "Not scheduled"}
          </strong>
          <p>
            {ready
              ? `Kodama will post each recap in channel ${server.recap_channel_id}.`
              : "Choose a Discord text channel and a UTC time to start daily recaps."}
          </p>
        </div>
        <div className="recap-rules">
          <h4>Included in recaps</h4>
          <ul>
            <li>Big conversations and decisions</li>
            <li>New lore moments and recurring jokes</li>
            <li>Notable member activity, excluding opted-out users</li>
          </ul>
        </div>
      </div>

      <div className="settings-grid" style={{ marginTop: 14 }}>
        <label>
          <span className="field-label">Daily recap time UTC</span>
          <input
            placeholder="21:00"
            value={recapTime}
            onChange={(event) => setRecapTime(event.target.value)}
          />
          <small>Use 24-hour UTC time, for example 21:00.</small>
        </label>
        <label>
          <span className="field-label">Recap channel ID</span>
          <input
            placeholder="Discord channel ID"
            value={recapChannel}
            onChange={(event) => setRecapChannel(event.target.value)}
          />
          <small>Paste the channel ID where daily recaps should post.</small>
        </label>
      </div>

      <div className="panel-footer">
        {saveError && <small className="save-error">{saveError}</small>}
        <button
          className="primary-button save-button"
          disabled={saving || !hasChanges}
          type="button"
          onClick={() =>
            onSave({
              ...server,
              recap_time: recapTime.trim() || null,
              recap_channel_id: recapChannel.trim() || null,
              has_recap: Boolean(recapTime.trim() && recapChannel.trim()),
            })
          }
        >
          <Zap size={18} aria-hidden="true" />
          {saving ? <LoadingDots label="Saving" tone="dark" /> : "Save changes"}
        </button>
      </div>
    </section>
  );
}

const BEHAVIOR_TOGGLES: Array<{
  key: keyof ServerSummary;
  label: string;
  description: string;
}> = [
  {
    key: "allow_profanity",
    label: "Allow profanity",
    description: "Kodama can use and echo swear words in replies.",
  },
  {
    key: "allow_roasting",
    label: "Allow roasting members",
    description: "Kodama can roast or flame members when asked.",
  },
  {
    key: "allow_organic_replies",
    label: "Organic replies",
    description: "Kodama can jump into conversations unprompted.",
  },
  {
    key: "allow_emoji_reactions",
    label: "Emoji reactions",
    description: "Kodama reacts to messages with emoji.",
  },
  {
    key: "allow_indexing",
    label: "Message indexing",
    description:
      "Kodama indexes messages into memory. Disable to pause learning.",
  },
  {
    key: "allow_recaps",
    label: "Daily recaps",
    description: "Kodama posts scheduled server recaps.",
  },
];

function PrivacyPanel({
  audit,
}: {
  audit: AuditEvent[];
}) {
  return (
    <div className="tab-panel">
      <section className="panel page-panel">
        <SectionHeader
          eyebrow="Guardrails"
          title="Member control"
          aside={<StatusPill>Protected</StatusPill>}
        />

        <div className="privacy-grid">
          <article className="privacy-card">
            <LockKeyhole size={20} aria-hidden="true" />
            <strong>Opt out</strong>
            <p>
              Members can remove themselves from future memory and summaries.
            </p>
            <code>/optout</code>
          </article>
          <article className="privacy-card">
            <Trash2 size={20} aria-hidden="true" />
            <strong>Forget a message</strong>
            <p>Mods and members can hard-delete a specific indexed message.</p>
            <code>/forget message_link</code>
          </article>
          <article className="privacy-card">
            <ShieldCheck size={20} aria-hidden="true" />
            <strong>Deleted messages</strong>
            <p>When Discord messages are deleted, Kodama removes them too.</p>
            <code>automatic</code>
          </article>
        </div>
      </section>

      <section className="panel page-panel activity-panel">
        <SectionHeader eyebrow="Audit" title="Recent activity" />
        <div className="activity-list">
          {audit.length === 0 ? (
            <EmptyState
              icon={<ShieldCheck size={24} aria-hidden="true" />}
              title="No recent privacy events"
              body="Opt-outs, forget requests, deleted messages, and setting changes will appear here."
            />
          ) : (
            audit.map((event) => (
              <article className="activity-item" key={event.id}>
                <ShieldCheck size={18} aria-hidden="true" />
                <div>
                  <h4>{formatAction(event.action)}</h4>
                  <p>
                    {event.target ?? "Server"} by{" "}
                    {event.actor_discord_id ?? "system"}
                  </p>
                </div>
                <span>{formatDate(event.created_at)}</span>
              </article>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

function SettingsPanel({
  server,
  saving,
  saveError,
  onSave,
  onSavePatch,
}: {
  server: ServerSummary;
  saving: boolean;
  saveError: string | null;
  onSave: (server: ServerSummary) => void;
  onSavePatch: (patch: SettingsPatch) => void;
}) {
  const personalityOptions: SelectOption[] = [
    {
      value: "mirror",
      label: "Mirror the server",
      detail: "Default, follows the learned server voice",
    },
    { value: "chill", label: "Chill", detail: "Low-key and concise" },
    { value: "hype", label: "Hype", detail: "Energetic without going corny" },
    {
      value: "unhinged",
      label: "Unhinged",
      detail: "Weirder and sharper, still bounded",
    },
    {
      value: "historian",
      label: "Historian",
      detail: "More factual and memory-forward",
    },
    {
      value: "custom",
      label: "Custom",
      detail: "Write your own personality direction",
    },
  ];
  const [personality, setPersonality] = useState(server.personality);
  const [customPersonality, setCustomPersonality] = useState(
    server.custom_personality ?? "",
  );
  const [mentionBehaviorSetting, setMentionBehaviorSetting] = useState(server.mention_behavior);
  const [emojiBank, setEmojiBank] = useState(server.emoji_bank.join(" "));
  const [lockEmojiBank, setLockEmojiBank] = useState(server.lock_emoji_bank);
  const [filterMode, setFilterMode] = useState<"all" | "whitelist" | "blacklist">(
    (server.channel_filter_mode as "all" | "whitelist" | "blacklist") ?? "all",
  );
  const [filterIds, setFilterIds] = useState<string[]>(server.channel_filter_ids ?? []);
  const [systemPrompt, setSystemPrompt] = useState(server.system_prompt ?? "");
  const [channels, setChannels] = useState<GuildChannel[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [channelSearch, setChannelSearch] = useState("");

  const [toggles, setToggles] = useState<Partial<Record<string, boolean>>>(
    Object.fromEntries(
      BEHAVIOR_TOGGLES.map((t) => [t.key, (server[t.key] as boolean) ?? true]),
    ),
  );

  useEffect(() => {
    setPersonality(server.personality);
    setCustomPersonality(server.custom_personality ?? "");
    setMentionBehaviorSetting(server.mention_behavior);
    setEmojiBank(server.emoji_bank.join(" "));
    setLockEmojiBank(server.lock_emoji_bank);
    setFilterMode((server.channel_filter_mode as "all" | "whitelist" | "blacklist") ?? "all");
    setFilterIds(server.channel_filter_ids ?? []);
    setSystemPrompt(server.system_prompt ?? "");
  }, [server]);

  // Fetch channels when filter mode changes away from "all"
  // Fetch channels on mount so they're ready when the user switches modes
  useEffect(() => {
    setChannelsLoading(true);
    fetchServerChannels(server.id)
      .then(setChannels)
      .catch(() => setChannels([]))
      .finally(() => setChannelsLoading(false));
  }, [server.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setToggles(
      Object.fromEntries(
        BEHAVIOR_TOGGLES.map((t) => [
          t.key,
          (server[t.key] as boolean) ?? true,
        ]),
      ),
    );
  }, [server]);

  function handleToggle(key: string, value: boolean) {
    const next = { ...toggles, [key]: value };
    setToggles(next);
    onSavePatch({ [key]: value } as SettingsPatch);
  }

  const hasChanges =
    personality !== server.personality ||
    customPersonality !== (server.custom_personality ?? "") ||
    mentionBehaviorSetting !== server.mention_behavior ||
    emojiBank.trim() !== server.emoji_bank.join(" ") ||
    lockEmojiBank !== server.lock_emoji_bank ||
    filterMode !== (server.channel_filter_mode ?? "all") ||
    JSON.stringify([...filterIds].sort()) !== JSON.stringify([...(server.channel_filter_ids ?? [])].sort()) ||
    systemPrompt !== (server.system_prompt ?? "");
  const customPersonalityRequired =
    personality === "custom" && customPersonality.trim().length < 8;

  return (
    <section className="panel page-panel tab-panel">
      <SectionHeader
        eyebrow="Controls"
        title="Personality & Schedule"
        aside={
          <StatusPill tone={hasChanges ? "warn" : "muted"}>
            {hasChanges ? "Unsaved" : "Saved"}
          </StatusPill>
        }
      />

      <div className="settings-grid">
        <label>
          <span className="field-label">Personality</span>
          <CustomSelect
            label="Personality"
            value={personality}
            options={personalityOptions}
            onChange={setPersonality}
          />
          <small>Mirror is the default server voice mode.</small>
        </label>

        <div className="reply-freq-field">
          <span className="field-label">Reply frequency</span>
          <div className="reply-freq-picker" role="radiogroup" aria-label="Reply frequency">
            {(
              [
                {
                  value: "silent",
                  label: "@mentions only",
                  description: "Only replies when directly @tagged. No follow-ups, no random replies.",
                },
                {
                  value: "contextual",
                  label: "Conversational",
                  description: "Replies to @mentions and follows threads it's been pulled into. Won't jump in unprompted.",
                },
                {
                  value: "chatty",
                  label: "Active",
                  description: "Everything above, plus occasionally jumps into conversations on its own when the topic feels relevant.",
                },
              ] as const
            ).map(({ value, label, description }) => {
              const active =
                mentionBehaviorSetting === value ||
                (value === "contextual" && (mentionBehaviorSetting === "concise" || mentionBehaviorSetting === "contextual")) ||
                (value === "chatty" && mentionBehaviorSetting === "chatty") ||
                (value === "silent" && mentionBehaviorSetting === "silent");
              return (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`reply-freq-option${active ? " reply-freq-active" : ""}`}
                  onClick={() => setMentionBehaviorSetting(value)}
                >
                  <strong>{label}</strong>
                  <span>{description}</span>
                </button>
              );
            })}
          </div>
        </div>

        <label className="wide-field custom-personality-field">
          <span className="field-label">Custom system prompt</span>
          <textarea
            maxLength={4000}
            rows={8}
            placeholder={`Optional — always applied on top of the personality mode above.\n\nExamples:\n• "You are a customer support bot for AcmeCo. The store sells sneakers, hats, and hoodies. For order issues, tell users to email support@acmeco.com."\n• "If somebody asks for a free dupe or how to dupe, ping @SupportRole with no further information."\n• "This is a gaming server. Hype members up around tournaments every Friday."`}
            value={systemPrompt}
            onChange={(event) => setSystemPrompt(event.target.value)}
          />
          <small>
            Server-specific instructions always injected into the bot's context. Use it to define the bot's role, describe your server, or set response rules. Works alongside any personality mode.
            {systemPrompt.length > 0 && (
              <span style={{ float: "right", opacity: 0.6 }}>{systemPrompt.length}/4000</span>
            )}
          </small>
        </label>

        {personality === "custom" && (
          <label className="wide-field custom-personality-field">
            <span className="field-label">Custom personality description</span>
            <textarea
              maxLength={2000}
              rows={5}
              placeholder='Describe the voice and tone for "Custom" mode. e.g. "dry and observant, lightly sarcastic, never too formal, uses lowercase"'
              value={customPersonality}
              onChange={(event) => setCustomPersonality(event.target.value)}
            />
            <small>
              Defines how Kodama writes when personality is set to Custom. Overrides the learned server voice.
              {customPersonality.length > 0 && (
                <span style={{ float: "right", opacity: 0.6 }}>{customPersonality.length}/2000</span>
              )}
            </small>
          </label>
        )}

        <div className="wide-field emoji-bank-field">
          <span className="field-label">Emoji bank</span>
          <div className="emoji-bank-row">
            <label className="emoji-learn-toggle">
              <div className="emoji-learn-toggle-text">
                <strong>{lockEmojiBank ? "Fixed" : "Learning"}</strong>
                <span>{lockEmojiBank ? "Only uses emojis listed" : "Picks up new emojis from server"}</span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!lockEmojiBank}
                className={`toggle-switch${!lockEmojiBank ? " toggle-on" : ""}`}
                onClick={() => setLockEmojiBank((v) => !v)}
              >
                <span className="toggle-thumb" />
              </button>
            </label>
            <div className="emoji-input-wrap">
              <input
                key={server.id + "_emoji_" + server.emoji_bank.join("")}
                placeholder="No emoji bank set"
                defaultValue={emojiBank}
                onChange={(event) => setEmojiBank(event.target.value)}
              />
              <small>Paste emoji separated by spaces. Kodama draws from these in reactions and replies.</small>
            </div>
          </div>
        </div>

        <div className="wide-field behavior-toggles">
          <h4>Behavior controls</h4>
          {BEHAVIOR_TOGGLES.map((t) => (
            <label key={t.key} className="toggle-row">
              <div className="toggle-info">
                <strong>{t.label}</strong>
                <span>{t.description}</span>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={toggles[t.key] ?? true}
                disabled={saving}
                className={`toggle-switch${(toggles[t.key] ?? true) ? " toggle-on" : ""}`}
                onClick={() =>
                  handleToggle(t.key as string, !(toggles[t.key] ?? true))
                }
              >
                <span className="toggle-thumb" />
              </button>
            </label>
          ))}
        </div>

        <div className="wide-field channel-filter-section">
          <h4>Channel filter</h4>
          <p className="channel-filter-desc">
            Control which channels Kodama is active in.
          </p>
          <div className="channel-filter-mode-picker">
            {(
              [
                { value: "all", label: "All channels", desc: "Kodama is active in every channel" },
                { value: "whitelist", label: "Whitelist", desc: "Kodama only responds in the selected channels" },
                { value: "blacklist", label: "Blacklist", desc: "Kodama stays silent in the selected channels" },
              ] as { value: "all" | "whitelist" | "blacklist"; label: string; desc: string }[]
            ).map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`channel-filter-mode-opt${filterMode === opt.value ? " channel-filter-mode-active" : ""}`}
                onClick={() => setFilterMode(opt.value)}
                disabled={saving}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className="channel-filter-mode-hint">
            {filterMode === "all" && "Kodama is active in every channel."}
            {filterMode === "whitelist" && "Kodama only responds in the selected channels."}
            {filterMode === "blacklist" && "Kodama stays silent in the selected channels."}
          </p>

          {filterMode !== "all" && (
            <div className="channel-list-wrap">
              <div className="channel-search-row">
                <input
                  className="channel-search-input"
                  type="text"
                  placeholder="Search channels…"
                  value={channelSearch}
                  onChange={(e) => setChannelSearch(e.target.value)}
                  disabled={channelsLoading || saving}
                />
              </div>
              <div className="channel-list">
                {channelsLoading ? (
                  <p className="channel-list-loading">Loading channels…</p>
                ) : channels.length === 0 ? (
                  <p className="channel-list-loading">No text channels found.</p>
                ) : (() => {
                  const q = channelSearch.toLowerCase().trim();
                  const visible = channels.filter((ch) =>
                    !q || ch.name.toLowerCase().includes(q),
                  );
                  if (visible.length === 0)
                    return <p className="channel-list-loading">No channels match.</p>;
                  // checked channels float to top
                  const sorted = [
                    ...visible.filter((ch) => filterIds.includes(ch.id)),
                    ...visible.filter((ch) => !filterIds.includes(ch.id)),
                  ];
                  return sorted.map((ch) => {
                    const checked = filterIds.includes(ch.id);
                    return (
                      <div
                        key={ch.id}
                        className={`channel-list-item${checked ? " channel-list-item-checked" : ""}`}
                        role="checkbox"
                        aria-checked={checked}
                        tabIndex={0}
                        onClick={() => {
                          if (saving) return;
                          setFilterIds((prev) =>
                            checked
                              ? prev.filter((id) => id !== ch.id)
                              : [...prev, ch.id],
                          );
                        }}
                        onKeyDown={(e) => {
                          if (e.key === " " || e.key === "Enter") {
                            e.preventDefault();
                            if (saving) return;
                            setFilterIds((prev) =>
                              checked
                                ? prev.filter((id) => id !== ch.id)
                                : [...prev, ch.id],
                            );
                          }
                        }}
                      >
                        <span className="channel-list-check" aria-hidden="true">
                          {checked ? "✓" : ""}
                        </span>
                        <span className="channel-list-name"># {ch.name}</span>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}
        </div>


      </div>

      <div className="panel-footer">
        {saveError && <small className="save-error">{saveError}</small>}
        <button
          className="primary-button save-button"
          disabled={saving || !hasChanges || customPersonalityRequired}
          type="button"
          onClick={() =>
            onSave({
              ...server,
              personality,
              custom_personality: customPersonality.trim() || null,
              mention_behavior: mentionBehaviorSetting,
              emoji_bank: emojiBank.trim() ? emojiBank.trim().split(/\s+/) : server.emoji_bank,
              lock_emoji_bank: lockEmojiBank,
              channel_filter_mode: filterMode,
              channel_filter_ids: filterIds,
              system_prompt: systemPrompt.trim() || null,
            })
          }
        >
          <Zap size={18} aria-hidden="true" />
          {saving ? (
            <LoadingDots label="Saving changes" tone="dark" />
          ) : (
            "Save changes"
          )}
        </button>
      </div>
    </section>
  );
}

function BotProfilePanel({
  server,
  saving,
  saveError,
  onSave,
  onUpgrade,
}: {
  server: ServerSummary;
  saving: boolean;
  saveError: string | null;
  onSave: (server: ServerSummary) => void;
  onUpgrade: () => void;
}) {
  const [displayName, setDisplayName] = useState(server.bot_display_name ?? "");
  const [avatarUrl, setAvatarUrl] = useState(server.bot_avatar_url ?? "");
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [description, setDescription] = useState(server.bot_description ?? "");
  const [banner, setBanner] = useState(server.bot_banner ?? "");
  const [bannerError, setBannerError] = useState<string | null>(null);
  const [bannerColor, setBannerColor] = useState(
    server.bot_banner?.startsWith("#") ? server.bot_banner : "#2fd889",
  );
  const [bannerMode, setBannerMode] = useState<"image" | "color">(
    server.bot_banner?.startsWith("#") ? "color" : "image",
  );
  const [showColorPicker, setShowColorPicker] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDisplayName(server.bot_display_name ?? "");
    setAvatarUrl(server.bot_avatar_url ?? "");
    setAvatarError(null);
    setDescription(server.bot_description ?? "");
    setBanner(server.bot_banner ?? "");
    setBannerError(null);
    setBannerColor(
      server.bot_banner?.startsWith("#") ? server.bot_banner : "#2fd889",
    );
    setBannerMode(server.bot_banner?.startsWith("#") ? "color" : "image");
  }, [server]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(e.target as Node)
      ) {
        setShowColorPicker(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const nextDisplayName = displayName.trim() || null;
  const nextAvatarUrl = avatarUrl.trim() || null;
  const previewAvatarUrl = nextAvatarUrl || defaultBotAvatarUrl;
  const nextDescription = description.trim() || null;
  const nextBanner =
    bannerMode === "color" ? bannerColor : banner.trim() || null;
  const previewBanner =
    bannerMode === "color" ? bannerColor : banner.trim() || defaultBannerUrl;

  const hasChanges =
    (nextDisplayName ?? "") !== (server.bot_display_name ?? "") ||
    (nextAvatarUrl ?? "") !== (server.bot_avatar_url ?? "") ||
    (nextDescription ?? "") !== (server.bot_description ?? "") ||
    (nextBanner ?? "") !== (server.bot_banner ?? "");

  function handleAvatarUpload(file: File | undefined): void {
    if (!file) return;
    if (file.size > maxAvatarBytes) {
      setAvatarError("Profile images must be 1 MB or smaller.");
      return;
    }
    setAvatarError(null);
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result === "string") setAvatarUrl(reader.result);
    });
    reader.readAsDataURL(file);
  }

  function handleBannerUpload(file: File | undefined): void {
    if (!file) return;
    if (file.size > maxAvatarBytes * 5) {
      setBannerError("Banner must be 5 MB or smaller.");
      return;
    }
    setBannerError(null);
    setBannerMode("image");
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result === "string") setBanner(reader.result);
    });
    reader.readAsDataURL(file);
  }

  return (
    <section className="panel page-panel tab-panel">
      <SectionHeader
        eyebrow="Bot profile"
        title="How Kodama appears here"
        aside={
          <StatusPill tone={hasChanges ? "warn" : "muted"}>
            {hasChanges ? "Unsaved" : "Saved"}
          </StatusPill>
        }
      />

      <div className="profile-config">
        <div className="bot-preview-card" aria-label="Bot profile preview">
          <span className="bot-preview-avatar">
            <img src={previewAvatarUrl} alt="" aria-hidden="true" />
          </span>
          <div>
            <p className="eyebrow">{server.name}</p>
            <h4>{nextDisplayName || "Kodama"}</h4>
            <span>{nextDescription || "No description set"}</span>
          </div>
        </div>

        <div className="settings-grid bot-settings-grid">
          <label>
            <span className="field-label">Display name</span>
            <input
              maxLength={32}
              placeholder="Kodama"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
            <small>
              Leave empty to use default. Syncs to this server's nickname.
            </small>
          </label>

          <label>
            <span className="field-label">Profile image</span>
            <input
              placeholder="https://... or upload below"
              value={avatarUrl.startsWith("data:") ? "" : avatarUrl}
              onChange={(event) => {
                setAvatarError(null);
                setAvatarUrl(event.target.value);
              }}
            />
            <div className="upload-row">
              <button
                className="secondary-button"
                type="button"
                onClick={() => avatarInputRef.current?.click()}
              >
                <Upload size={16} aria-hidden="true" /> Upload image
              </button>
              <input
                ref={avatarInputRef}
                accept="image/png,image/jpeg,image/webp"
                className="file-input"
                type="file"
                onChange={(event) => {
                  handleAvatarUpload(event.target.files?.[0]);
                  event.target.value = "";
                }}
              />
              <small>PNG, JPG, or WebP. Max 1 MB.</small>
            </div>
            {avatarError && (
              <small className="field-error">{avatarError}</small>
            )}
          </label>

          <LockedSection
            requiredTier="chronicle"
            currentTier={server.tier}
            onUpgrade={onUpgrade}
          >
            <label>
              <span className="field-label">Description</span>
              <textarea
                maxLength={350}
                rows={4}
                placeholder="Leave empty for default Kodama description."
                value={description}
                disabled={!meetsRequiredTier(server.tier, "chronicle")}
                onChange={(event) => setDescription(event.target.value)}
              />
              <small>{description.length}/350 characters</small>
            </label>
          </LockedSection>

        </div>
      </div>

      {/* Banner section */}
      <div className="banner-section">
        <h4 className="banner-section-label">Server banner</h4>
        <div
          className="banner-preview"
          style={{
            background:
              bannerMode === "color" ? (previewBanner ?? "#2fd889") : undefined,
            backgroundImage:
              bannerMode === "image" && previewBanner
                ? `url(${previewBanner})`
                : undefined,
          }}
          aria-label="Banner preview"
        />
        <div className="banner-controls">
          <button
            className="secondary-button"
            type="button"
            onClick={() => bannerInputRef.current?.click()}
          >
            <Upload size={16} aria-hidden="true" /> Replace image
          </button>
          <input
            ref={bannerInputRef}
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="file-input"
            type="file"
            onChange={(event) => {
              handleBannerUpload(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
          <div className="color-picker-wrap" ref={colorPickerRef}>
            <button
              className="secondary-button color-swatch-btn"
              type="button"
              onClick={() => setShowColorPicker((v) => !v)}
            >
              <span
                className="color-swatch"
                style={{ background: bannerColor }}
              />
              Set color
            </button>
            {showColorPicker && (
              <div className="color-picker-popup">
                <p className="color-picker-label">Pick a color</p>
                <input
                  type="color"
                  value={bannerColor}
                  className="color-picker-input"
                  onChange={(e) => {
                    setBannerColor(e.target.value);
                    setBannerMode("color");
                  }}
                />
                <p className="color-picker-hex">{bannerColor}</p>
              </div>
            )}
          </div>
          {bannerMode === "image" && banner && (
            <button
              className="secondary-button"
              type="button"
              onClick={() => {
                setBanner("");
                setBannerMode("color");
              }}
            >
              Remove image
            </button>
          )}
        </div>
        {bannerError && <small className="field-error">{bannerError}</small>}
      </div>

      <div className="panel-footer">
        {saveError && <small className="save-error">{saveError}</small>}
        <button
          className="primary-button save-button"
          disabled={saving || !hasChanges}
          type="button"
          onClick={() =>
            onSave({
              ...server,
              bot_display_name: nextDisplayName,
              bot_avatar_url: nextAvatarUrl || null,
              bot_description: nextDescription,
              bot_banner: nextBanner,
            })
          }
        >
          <Bot size={18} aria-hidden="true" />
          {saving ? (
            <LoadingDots label="Saving profile" tone="dark" />
          ) : (
            "Save profile"
          )}
        </button>
      </div>
    </section>
  );
}

const FREE_FEATURES = [
  "@mention replies",
  "Privacy controls (/optout, /forget)",
  "Dashboard overview",
  "Behavior controls & guardrails",
  "Ask Kodama: 5 messages/day",
];
const INDEX_FEATURES = [
  "Server scan (recent history)",
  "Daily recaps + /recap command",
  "/whois member profiles",
  "Organic replies, emoji reactions, flame mode",
  "All personality modes + custom",
  "Emoji bank & channel filtering",
  "Bot profile: display name, avatar, status, banner",
  "Ask Kodama: 50 messages/day",
];
const CHRONICLE_FEATURES = [
  "Deep scan (full server history)",
  "Bot profile description",
  "Inside joke detection + reports feed",
  "Ask Kodama: unlimited",
  "Voice profile learning",
];

function UpgradePanel({
  tier,
  onUpgrade,
  onManageBilling,
  pendingTier,
}: {
  tier: string;
  onUpgrade: (tier: "index" | "chronicle") => void;
  onManageBilling: () => void;
  pendingTier: "index" | "chronicle" | "portal" | null;
}) {
  function TierCard({
    id,
    name,
    priceNum,
    tagline,
    features,
    inheritsFrom,
    badge,
    accent,
    current,
  }: {
    id: "free" | "index" | "chronicle";
    name: string;
    priceNum: number;
    tagline: string;
    features: string[];
    inheritsFrom?: string;
    badge?: string;
    accent?: "mint" | "gold";
    current?: boolean;
  }) {
    const tierRank = (t: string) => (t === "chronicle" ? 2 : t === "index" ? 1 : 0);
    const userRank = tierRank(tier);
    const cardRank = tierRank(id);
    const canUpgrade = !current && id !== "free" && cardRank > userRank;
    const isPending = pendingTier === id || pendingTier === "portal";
    return (
      <div
        className={[
          "tier-card",
          accent ? `tier-card-${accent}` : "",
          current ? "tier-card-current" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {badge && !current && <div className={`tier-badge tier-badge-${accent ?? "mint"}`}>{badge}</div>}
        {current && <div className="tier-badge tier-badge-current">Current plan</div>}

        <div className="tier-card-top">
          <strong className="tier-name">{name}</strong>
          <div className="tier-price-block">
            {priceNum === 0 ? (
              <span className="tier-price-num">Free</span>
            ) : (
              <>
                <span className="tier-price-num">${priceNum}</span>
                <span className="tier-price-period">/ mo</span>
              </>
            )}
          </div>
          <p className="tier-tagline">{tagline}</p>
        </div>

        <ul className="tier-feature-list">
          {inheritsFrom && (
            <li className="tier-inherits">
              <Check size={14} aria-hidden="true" />
              Everything in {inheritsFrom}
            </li>
          )}
          {inheritsFrom && features.length > 0 && (
            <li className="tier-feature-divider" aria-hidden="true" />
          )}
          {features.map((f) => (
            <li key={f}>
              <Check size={14} aria-hidden="true" />
              {f}
            </li>
          ))}
        </ul>

        <div className="tier-card-cta">
          {canUpgrade && (
            <button
              type="button"
              className={accent === "mint" ? "primary-button" : accent === "gold" ? "tier-cta-gold" : "secondary-button"}
              disabled={Boolean(pendingTier)}
              onClick={() => onUpgrade(id as "index" | "chronicle")}
            >
              {isPending ? (
                <LoadingDots label="Opening checkout" tone="dark" />
              ) : (
                `Upgrade to ${name}`
              )}
            </button>
          )}
          {current && (
            <>
              <p className="tier-current-label">You&rsquo;re on this plan</p>
              {id !== "free" && (
                <button
                  type="button"
                  className="secondary-button"
                  disabled={Boolean(pendingTier)}
                  onClick={onManageBilling}
                >
                  {pendingTier === "portal" ? (
                    <LoadingDots label="Opening billing" />
                  ) : (
                    "Manage billing"
                  )}
                </button>
              )}
            </>
          )}
          {!canUpgrade && !current && id === "free" && (
            <p className="tier-current-label tier-free-note">No credit card needed</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <section className="upgrade-panel">
      <p className="upgrade-subtitle">
        Pick the plan that fits your server &mdash; upgrade or cancel any time.
      </p>
      <div className="upgrade-tiers">
        <TierCard
          id="free"
          name="Free"
          priceNum={0}
          tagline="Get started, no commitment."
          features={FREE_FEATURES}
          current={tier === "free"}
        />
        <TierCard
          id="index"
          name="Kodama Index"
          priceNum={5}
          tagline="Full memory and personality for active servers."
          features={INDEX_FEATURES}
          inheritsFrom="Free"
          badge="Most popular"
          accent="mint"
          current={tier === "index"}
        />
        <TierCard
          id="chronicle"
          name="Kodama Chronicle"
          priceNum={15}
          tagline="For servers that live and breathe their own lore."
          features={CHRONICLE_FEATURES}
          inheritsFrom="Kodama Index"
          badge="Premium"
          accent="gold"
          current={tier === "chronicle"}
        />
      </div>
    </section>
  );
}

function BillingPage({
  me,
  onNavigate,
  showToast,
}: {
  me: MeResponse | null;
  onNavigate: (page: PageId) => void;
  showToast: (message: string, tone?: "neutral" | "success" | "error") => void;
}) {
  const tier = me?.servers[0]?.tier ?? "free";
  const [pendingTier, setPendingTier] = useState<
    "index" | "chronicle" | "portal" | null
  >(null);

  async function openCheckout(nextTier: "index" | "chronicle") {
    setPendingTier(nextTier);
    try {
      const { url } = await createCheckoutSession(nextTier);
      window.location.href = url;
    } catch (err) {
      showToast(
        err instanceof ApiError && err.message
          ? err.message
          : "Could not start checkout. Try again in a moment.",
        "error",
      );
      setPendingTier(null);
    }
  }

  async function openBillingPortal() {
    setPendingTier("portal");
    try {
      const { url } = await createBillingPortalSession();
      window.location.href = url;
    } catch (err) {
      showToast(
        err instanceof ApiError && err.message
          ? err.message
          : "Could not open billing portal. Try again in a moment.",
        "error",
      );
      setPendingTier(null);
    }
  }

  return (
    <main className="marketing-shell">
      <MarketingNav me={me} onNavigate={onNavigate} />
      <section className="billing-page-content">
        <div className="billing-page-header">
          <p className="eyebrow">Account</p>
          <h2>Plans &amp; Billing</h2>
        </div>
        <UpgradePanel
          tier={tier}
          onUpgrade={openCheckout}
          onManageBilling={openBillingPortal}
          pendingTier={pendingTier}
        />
      </section>
    </main>
  );
}

/**
 * Handles /api/auth/callback — Discord redirects here after OAuth.
 * CF Pages serves index.html for this path; we exchange the code for a token.
 */
function OAuthCallbackPage() {
  const [status, setStatus] = useState<"loading" | "error">("loading");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    if (!code || !state) {
      setStatus("error");
      return;
    }
    exchangeOAuthCode(code, state)
      .then(({ token, returnTo }) => {
        storeToken(token);
        window.location.replace(safeReturnTo(returnTo));
      })
      .catch(() => setStatus("error"));
  }, []);

  return (
    <main
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        gap: "12px",
      }}
    >
      {status === "loading" ? (
        <div className="auth-loading">
          <LoadingDots label="Signing in" />
          <p>Signing in...</p>
        </div>
      ) : (
        <>
          <p>
            Sign-in failed. <a href="/">Go back</a>
          </p>
        </>
      )}
    </main>
  );
}

/**
 * Handles the /auth/finish route after Discord OAuth callback.
 * Reads the session token from the URL hash, stores it, and redirects.
 */
function safeReturnTo(value: string | null): string {
  if (!value) return "/servers";
  // Must be a relative path, not a protocol-relative or absolute URL
  if (!value.startsWith("/") || value.startsWith("//")) return "/servers";
  if (value.length > 180) return "/servers";
  return value;
}

function AuthFinishPage() {
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    const params = new URLSearchParams(hash);
    const token = params.get("token");
    const returnTo = safeReturnTo(
      new URLSearchParams(window.location.search).get("return_to"),
    );
    if (token) {
      storeToken(decodeURIComponent(token));
    }
    // Remove the token hash from browser history before navigating
    history.replaceState(null, "", window.location.pathname + window.location.search);
    window.location.replace(returnTo);
  }, []);

  return (
    <main
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
      }}
    >
      <div className="auth-loading">
        <LoadingDots label="Signing in" />
        <p>Signing in...</p>
      </div>
    </main>
  );
}

// ── Usage Dashboard Page ─────────────────────────────────────────────────────

type UsageRow = {
  provider: string;
  model: string;
  input_tokens: string;
  output_tokens: string;
  total_tokens: string;
  cost_usd: string;
  calls: number;
  first_seen: string;
  last_seen: string;
};

type HourlyRow = {
  hour: string;
  provider: string;
  cost_usd: string;
  total_tokens: string;
};

type UsageData = {
  window: string;
  rows: UsageRow[];
  total_cost_usd: number;
};

type HourlyData = {
  window: string;
  rows: HourlyRow[];
};

const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

async function fetchUsage(window: string): Promise<UsageData> {
  const res = await fetch(`${apiBase}/api/usage?window=${window}`);
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json() as Promise<UsageData>;
}

async function fetchHourly(window: string): Promise<HourlyData> {
  // The hourly endpoint doesn't support "all" — fall back to 30d for chart display
  const effectiveWindow = window === "all" ? "30d" : window;
  const res = await fetch(`${apiBase}/api/usage/hourly?window=${effectiveWindow}`);
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json() as Promise<HourlyData>;
}

function formatUsd(n: number | string, decimals = 4): string {
  return `$${Number(n).toFixed(decimals)}`;
}

function formatNumber(n: number | string): string {
  return Number(n).toLocaleString();
}

function formatHour(iso: string, window: string): string {
  try {
    const d = new Date(iso);
    if (window === "24h") {
      return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
    }
    return d.toLocaleDateString(undefined, { month: "2-digit", day: "2-digit" });
  } catch {
    return iso;
  }
}

function formatYAxisTick(value: number): string {
  if (value === 0) return "$0";
  return value < 0.01 ? `$${value.toFixed(6)}` : `$${value.toFixed(4)}`;
}

function UsagePage({ me, onNavigate }: { me: MeResponse | null; onNavigate: (page: PageId) => void }) {
  const [selectedWindow, setSelectedWindow] = useState("24h");
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const [hourlyData, setHourlyData] = useState<HourlyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const loadData = useCallback(async (win: string) => {
    setLoading(true);
    setError(null);
    try {
      const [usage, hourly] = await Promise.all([
        fetchUsage(win),
        fetchHourly(win),
      ]);
      setUsageData(usage);
      setHourlyData(hourly);
      setLastRefreshed(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load usage data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData(selectedWindow);
  }, [selectedWindow, loadData]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const timer = window.setInterval(() => {
      void loadData(selectedWindow);
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [selectedWindow, loadData]);

  // Build chart data: merge hourly rows by hour (sum across providers)
  const chartData = useMemo(() => {
    if (!hourlyData?.rows.length) return [];
    const byHour = new Map<string, { hour: string; _isoHour: string; anthropic: number; openai: number; voyage: number; other: number }>();
    for (const row of hourlyData.rows) {
      const key = row.hour;
      if (!byHour.has(key)) {
        byHour.set(key, { hour: formatHour(key, selectedWindow), _isoHour: key, anthropic: 0, openai: 0, voyage: 0, other: 0 });
      }
      const entry = byHour.get(key)!;
      const cost = Number(row.cost_usd);
      if (row.provider === "anthropic") entry.anthropic += cost;
      else if (row.provider === "openai") entry.openai += cost;
      else if (row.provider === "voyage") entry.voyage += cost;
      else entry.other += cost;
    }
    return Array.from(byHour.values());
  }, [hourlyData, selectedWindow]);

  const windows = [
    { id: "24h", label: "24h" },
    { id: "7d", label: "7d" },
    { id: "30d", label: "30d" },
    { id: "all", label: "All time" },
  ];

  return (
    <main className="marketing-shell">
      <MarketingNav me={me} onNavigate={onNavigate} />

      <div className="admin-shell">
        <div className="admin-header">
          <div>
            <p className="eyebrow">Operations</p>
            <h2>API Token Usage</h2>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {lastRefreshed && (
              <span style={{ fontSize: 12, color: "var(--text-muted, #888)" }}>
                Updated {lastRefreshed.toLocaleTimeString()}
              </span>
            )}
            <div style={{ display: "flex", gap: 6 }}>
              {windows.map((w) => (
                <button
                  key={w.id}
                  type="button"
                  className={selectedWindow === w.id ? "primary-button" : "secondary-button"}
                  style={{ padding: "4px 14px", fontSize: 13 }}
                  onClick={() => setSelectedWindow(w.id)}
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && <p className="admin-error" style={{ padding: 16 }}>{error}</p>}

        {loading && !usageData && (
          <div className="admin-loading">
            <LoadingDots label="Loading usage data" />
          </div>
        )}

        {usageData && (
          <>
            {/* Total cost card */}
            <div className="admin-stats-grid" style={{ marginBottom: 24 }}>
              <article className="admin-stat-card" style={{ gridColumn: "span 2" }}>
                <p>Total cost ({selectedWindow})</p>
                <strong style={{ fontSize: 32 }}>{formatUsd(usageData.total_cost_usd, 4)}</strong>
                <span>{usageData.rows.reduce((s, r) => s + r.calls, 0).toLocaleString()} API calls</span>
              </article>
              <article className="admin-stat-card">
                <p>Models used</p>
                <strong>{usageData.rows.length}</strong>
                <span>distinct model/provider pairs</span>
              </article>
              <article className="admin-stat-card">
                <p>Total tokens</p>
                <strong>
                  {formatNumber(
                    usageData.rows.reduce((s, r) => s + Number(r.total_tokens), 0),
                  )}
                </strong>
                <span>input + output combined</span>
              </article>
            </div>

            {/* Hourly chart */}
            <div className="panel" style={{ padding: 24, marginBottom: 24, borderRadius: 12 }}>
              <div className="panel-header" style={{ marginBottom: 16 }}>
                <div>
                  <p className="eyebrow">Cost over time</p>
                  <h3>Hourly cost breakdown (USD)</h3>
                </div>
              </div>
              {chartData.length < 2 ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 220, color: "var(--text-muted, #888)", fontSize: 14, textAlign: "center", padding: "0 24px" }}>
                  Not enough data yet — chart will appear once Kodama has logged a few API calls.
                </div>
              ) : (
                <div style={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 8, right: 24, left: 8, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                      <XAxis
                        dataKey="hour"
                        tick={{ fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        interval={"preserveStartEnd"}
                      />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={formatYAxisTick}
                        width={80}
                      />
                      <Tooltip
                        formatter={(value: unknown, name: unknown) => {
                          const v = Number(value);
                          const n = String(name);
                          return [v < 0.01 ? `$${v.toFixed(6)}` : `$${v.toFixed(4)}`, n.charAt(0).toUpperCase() + n.slice(1)];
                        }}
                        labelFormatter={(label: unknown) => `Hour: ${String(label)}`}
                        contentStyle={{ fontSize: 12 }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="anthropic" name="Anthropic" stroke="#6b7cff" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="voyage" name="Voyage" stroke="#22d3a5" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="openai" name="OpenAI" stroke="#f97316" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="other" name="Other" stroke="#a78bfa" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Cost by model table */}
            <div className="admin-table-wrap" style={{ borderRadius: 12 }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Provider</th>
                    <th>Calls</th>
                    <th>Input tokens</th>
                    <th>Output tokens</th>
                    <th>Total tokens</th>
                    <th>Cost (USD)</th>
                    <th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {usageData.rows.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="admin-empty-row">
                        No usage data in this window. API calls will appear here once the bot uses Claude or embeddings.
                      </td>
                    </tr>
                  ) : (
                    usageData.rows.map((row) => (
                      <tr key={`${row.provider}:${row.model}`} className="admin-row">
                        <td><strong>{row.model}</strong></td>
                        <td>
                          <span className={`status-pill ${row.provider === "anthropic" ? "status-good" : row.provider === "voyage" ? "admin-tier-index" : "status-muted"}`}>
                            {row.provider}
                          </span>
                        </td>
                        <td className="admin-td-num">{row.calls.toLocaleString()}</td>
                        <td className="admin-td-num">{formatNumber(row.input_tokens)}</td>
                        <td className="admin-td-num">{formatNumber(row.output_tokens)}</td>
                        <td className="admin-td-num">{formatNumber(row.total_tokens)}</td>
                        <td className="admin-td-num"><strong>{formatUsd(row.cost_usd, 6)}</strong></td>
                        <td className="admin-td-date">{formatDate(row.last_seen)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <MarketingFooter onNavigate={onNavigate} />
    </main>
  );
}

function App() {
  const [page, setPage] = useState<PageId>(getInitialPage);
  const [activeTab, setActiveTab] = useState<TabId>(getInitialTab);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [selectedServerId, setSelectedServerId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ServerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [memoryWorking, setMemoryWorking] = useState(false);
  const [memoryError, setMemoryError] = useState<string | null>(null);
  const [askOpen, setAskOpen] = useState(false);
  const [askMessages, setAskMessages] = useState<AskMessage[]>([]);
  const [askSending, setAskSending] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [scanRunning, setScanRunning] = useState(false);
  const [appToast, setAppToast] = useState<{
    message: string;
    tone: "neutral" | "success" | "error";
    icon?: ReactNode;
  } | null>(null);
  const [appToastDismissing, setAppToastDismissing] = useState(false);
  const appToastTimerRef = useRef<number | null>(null);
  const [authError, setAuthError] = useState(false);
  const [authToastDismissing, setAuthToastDismissing] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    function showAuthError() {
      setAuthToastDismissing(false);
      setAuthError(true);
    }

    window.addEventListener("kodama:auth-start-error", showAuthError);
    return () =>
      window.removeEventListener("kodama:auth-start-error", showAuthError);
  }, []);

  useEffect(() => {
    if (!authError) return;
    const timeout = window.setTimeout(dismissAuthToast, 5200);
    return () => window.clearTimeout(timeout);
  }, [authError]);

  useEffect(() => {
    function syncFromLocation() {
      setPage(getInitialPage());
      setActiveTab(getInitialTab());
      setAuthError(false);
      setAuthToastDismissing(false);
    }

    window.addEventListener("popstate", syncFromLocation);
    return () => window.removeEventListener("popstate", syncFromLocation);
  }, []);

  useEffect(() => {
    if (page !== "dashboard") return;
    const query = selectedServerId ? `?server=${selectedServerId}` : "";
    const nextHash = `#${activeTab}`;
    if (window.location.search !== query || window.location.hash !== nextHash) {
      window.history.replaceState(null, "", `/dashboard${query}${nextHash}`);
    }
  }, [activeTab, page, selectedServerId]);

  useEffect(() => {
    if (page === "auth-finish" || page === "oauth-callback") return;
    getMe()
      .then((data) => {
        setMe(data);
        const urlServerId = new URLSearchParams(window.location.search).get(
          "server",
        );
        const requestedServer = data.servers.find(
          (server) => server.id === urlServerId,
        );
        // Admins can open any server by ID even if they don't own it —
        // trust the URL param directly instead of falling back to their own servers.
        const resolvedId = data.isAdmin && urlServerId
          ? urlServerId
          : (requestedServer?.id ?? data.servers[0]?.id ?? null);
        setSelectedServerId(resolvedId);
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 401) {
          if (page === "servers" || page === "dashboard") {
            void initiateLogin(currentReturnTo());
          }
          return;
        }
      })
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => {
    if (!selectedServerId) return;
    setAskMessages([]);
    setAskError(null);
    setLoading(true);
    setDashboardError(null);
    getServer(selectedServerId)
      .then(setDetail)
      .catch((error: unknown) => {
        setDetail(null);
        if (error instanceof ApiError && error.status === 401) {
          void initiateLogin(currentReturnTo());
          return;
        }
        if (error instanceof ApiError && [403, 404].includes(error.status)) {
          navigate("servers");
          return;
        }
        setDashboardError("Discord is taking a moment. Try this server again.");
      })
      .finally(() => setLoading(false));
  }, [loadAttempt, page, selectedServerId]);

  useEffect(() => {
    if (page === "dashboard" && !loading && me && !selectedServerId) {
      navigate("servers");
    }
  }, [loading, me, page, selectedServerId]);

  const selectedServer = useMemo(
    () => me?.servers.find((server) => server.id === selectedServerId) ?? null,
    [me, selectedServerId],
  );

  const title = {
    overview: "Dashboard",
    memory: "Memory",
    recaps: "Schedule",
    privacy: "Guardrails",
    settings: "Controls",
    profile: "Bot Profile",
  }[activeTab];

  function showToast(
    message: string,
    tone: "neutral" | "success" | "error" = "success",
    icon?: ReactNode,
  ): void {
    if (appToastTimerRef.current) window.clearTimeout(appToastTimerRef.current);
    setAppToastDismissing(false);
    setAppToast({ message, tone, icon });
    appToastTimerRef.current = window.setTimeout(() => {
      setAppToastDismissing(true);
      appToastTimerRef.current = window.setTimeout(() => {
        setAppToast(null);
        setAppToastDismissing(false);
      }, 260);
    }, 4000);
  }

  function dismissAppToast(): void {
    if (appToastTimerRef.current) window.clearTimeout(appToastTimerRef.current);
    setAppToastDismissing(true);
    appToastTimerRef.current = window.setTimeout(() => {
      setAppToast(null);
      setAppToastDismissing(false);
    }, 260);
  }

  function navigate(pageId: PageId): void {
    const pathMap: Partial<Record<PageId, string>> = {
      home: "/",
      commands: "/commands",
      "privacy-policy": "/privacy",
    };
    const path = pathMap[pageId] ?? `/${pageId}`;
    window.history.pushState(null, "", path);
    setPage(pageId);
    setAuthError(false);
    setAuthToastDismissing(false);
    if (pageId === "dashboard") {
      setActiveTab("overview");
    }
  }

  useEffect(() => {
    if (
      page === "home" ||
      page === "info" ||
      page === "commands" ||
      page === "tos" ||
      page === "privacy-policy"
    ) {
      const targetPath =
        page === "home"
          ? "/"
          : page === "privacy-policy"
            ? "/privacy"
            : `/${page}`;
      window.location.replace(`${marketingOrigin}${targetPath}`);
    }
  }, [page]);

  function dismissAuthToast(): void {
    setAuthToastDismissing(true);
    window.setTimeout(() => {
      setAuthError(false);
      setAuthToastDismissing(false);
    }, 260);
  }

  function manageServer(serverId: string): void {
    setSelectedServerId(serverId);
    setDetail(null);
    setDashboardError(null);
    setSaveError(null);
    setMemoryError(null);
    window.history.pushState(
      null,
      "",
      `/dashboard?server=${serverId}#overview`,
    );
    setActiveTab("overview");
    setPage("dashboard");
  }

  async function saveServer(nextServer: ServerSummary): Promise<void> {
    if (!detail) return;
    const currentServer = detail.server;
    const patch: SettingsPatch = {};
    if (nextServer.personality !== currentServer.personality) {
      patch.personality = nextServer.personality;
    }
    if (nextServer.custom_personality !== currentServer.custom_personality) {
      patch.custom_personality = nextServer.custom_personality;
    }
    if (nextServer.flame_enabled !== currentServer.flame_enabled) {
      patch.flame_enabled = nextServer.flame_enabled;
    }
    if (nextServer.recap_channel_id !== currentServer.recap_channel_id) {
      patch.recap_channel_id = nextServer.recap_channel_id;
    }
    if (nextServer.recap_time !== currentServer.recap_time) {
      patch.recap_time = nextServer.recap_time;
    }
    if (nextServer.bot_display_name !== currentServer.bot_display_name) {
      patch.bot_display_name = nextServer.bot_display_name;
    }
    if (nextServer.bot_avatar_url !== currentServer.bot_avatar_url) {
      patch.bot_avatar_url = nextServer.bot_avatar_url;
    }
    if (nextServer.bot_status !== currentServer.bot_status) {
      patch.bot_status = nextServer.bot_status;
    }
    if (nextServer.bot_description !== currentServer.bot_description) {
      patch.bot_description = nextServer.bot_description;
    }
    if (nextServer.bot_banner !== currentServer.bot_banner) {
      patch.bot_banner = nextServer.bot_banner;
    }
    if (nextServer.mention_behavior !== currentServer.mention_behavior) {
      patch.mention_behavior = nextServer.mention_behavior;
    }
    if (nextServer.emoji_bank.join(" ") !== currentServer.emoji_bank.join(" ")) {
      patch.emoji_bank = nextServer.emoji_bank;
    }
    if (nextServer.lock_emoji_bank !== currentServer.lock_emoji_bank) {
      patch.lock_emoji_bank = nextServer.lock_emoji_bank;
    }
    if (nextServer.channel_filter_mode !== currentServer.channel_filter_mode) {
      patch.channel_filter_mode = nextServer.channel_filter_mode;
    }
    if (
      JSON.stringify([...(nextServer.channel_filter_ids ?? [])].sort()) !==
      JSON.stringify([...(currentServer.channel_filter_ids ?? [])].sort())
    ) {
      patch.channel_filter_ids = nextServer.channel_filter_ids;
    }
    if ((nextServer.system_prompt ?? null) !== (currentServer.system_prompt ?? null)) {
      patch.system_prompt = nextServer.system_prompt;
    }
    if (Object.keys(patch).length === 0) return;

    setSaving(true);
    setSaveError(null);
    try {
      const result = await updateSettings(nextServer.id, patch);
      if (result.server) {
        setDetail({ ...detail, server: result.server });
      }
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "Could not save settings",
      );
    } finally {
      setSaving(false);
    }
  }

  async function saveSettingsPatch(patch: SettingsPatch): Promise<void> {
    if (!detail) return;
    setSaving(true);
    setSaveError(null);
    try {
      const result = await updateSettings(detail.server.id, patch);
      if (result.server) {
        setDetail({ ...detail, server: result.server });
      }
    } catch {
      setSaveError("Failed to save. Try again.");
    } finally {
      setSaving(false);
    }
  }

  async function addMemory(entry: {
    title: string;
    summary: string;
  }): Promise<void> {
    if (!detail) return;
    setMemoryWorking(true);
    setMemoryError(null);
    try {
      const result = await createLore(detail.server.id, entry);
      const alreadySaved = detail.lore.some(
        (item) => item.id === result.lore.id,
      );
      setDetail({
        ...detail,
        lore: [
          result.lore,
          ...detail.lore.filter((item) => item.id !== result.lore.id),
        ],
        overview: {
          ...detail.overview,
          loreCount: alreadySaved
            ? detail.overview.loreCount
            : detail.overview.loreCount + 1,
          auditCountToday: detail.overview.auditCountToday + 1,
        },
      });
    } catch (error) {
      setMemoryError(
        error instanceof Error ? error.message : "Could not save memory",
      );
      throw error;
    } finally {
      setMemoryWorking(false);
    }
  }

  async function removeMemory(entry: LoreEntry): Promise<void> {
    if (!detail) return;
    setMemoryWorking(true);
    setMemoryError(null);
    try {
      await deleteLore(detail.server.id, entry.id);
      setDetail({
        ...detail,
        lore: detail.lore.filter((item) => item.id !== entry.id),
        overview: {
          ...detail.overview,
          loreCount: Math.max(0, detail.overview.loreCount - 1),
          auditCountToday: detail.overview.auditCountToday + 1,
        },
      });
    } catch (error) {
      setMemoryError(
        error instanceof Error ? error.message : "Could not delete memory",
      );
      throw error;
    } finally {
      setMemoryWorking(false);
    }
  }

  async function handleDismissReport(report: ServerReport): Promise<void> {
    if (!detail) return;
    try {
      await dismissReport(detail.server.id, report.id);
      setDetail({
        ...detail,
        reports: detail.reports.filter((r) => r.id !== report.id),
      });
    } catch (error) {
      setMemoryError(
        error instanceof Error ? error.message : "Could not dismiss report",
      );
    }
  }

  async function handlePromoteReport(report: ServerReport): Promise<void> {
    if (!detail) return;
    setMemoryWorking(true);
    setMemoryError(null);
    try {
      const result = await promoteReport(detail.server.id, report.id);
      setDetail({
        ...detail,
        reports: detail.reports.filter((r) => r.id !== report.id),
        lore: result.lore,
        overview: {
          ...detail.overview,
          loreCount: result.lore.length,
        },
      });
    } catch (error) {
      setMemoryError(
        error instanceof Error ? error.message : "Could not pin to lore",
      );
    } finally {
      setMemoryWorking(false);
    }
  }

  async function handleScan(): Promise<void> {
    if (!detail || scanRunning) return;
    setScanRunning(true);
    try {
      await scanServerHistory(detail.server.id);
      showToast(
        "Scan started — this may take a few minutes",
        "success",
        <History size={18} aria-hidden="true" />,
      );
    } catch {
      showToast("Scan failed. Try again.", "error");
    } finally {
      setScanRunning(false);
    }
  }

  async function sendAskQuestion(question: string): Promise<void> {
    if (!detail) return;
    const nextMessages: AskMessage[] = [
      ...askMessages,
      { role: "user", content: question },
    ];
    setAskMessages(nextMessages);
    setAskSending(true);
    setAskError(null);
    try {
      const result = await askKodama(detail.server.id, question, askMessages);
      setAskMessages([
        ...nextMessages,
        { role: "kodama", content: result.answer },
      ]);
    } catch (error) {
      const isRateLimited = error instanceof ApiError && error.status === 429;
      setAskError(
        isRateLimited
          ? (error as ApiError).message
          : error instanceof Error
            ? error.message
            : "Kodama could not answer",
      );
      if (!isRateLimited) {
        setAskMessages([
          ...nextMessages,
          {
            role: "kodama",
            content: "i couldn't pull that from memory right now",
          },
        ]);
      }
    } finally {
      setAskSending(false);
    }
  }

  function renderPanel(): ReactNode {
    if (!detail) return null;
    const tier = detail.server.tier;
    const goUpgrade = () => navigate("billing");

    if (activeTab === "memory")
      return (
        <MemoryPanel
          reports={detail.reports}
          lore={detail.lore}
          working={memoryWorking}
          error={memoryError}
          tier={tier}
          onAsk={() => setAskOpen(true)}
          onCreate={addMemory}
          onDelete={removeMemory}
          onDismissReport={handleDismissReport}
          onPromoteReport={handlePromoteReport}
          onUpgrade={goUpgrade}
          scanRunning={scanRunning}
          onScan={handleScan}
        />
      );

    if (activeTab === "recaps")
      return (
        <LockedSection requiredTier="index" currentTier={tier} onUpgrade={goUpgrade}>
          <RecapsPanel server={detail.server} saving={saving} saveError={saveError} onSave={saveServer} />
        </LockedSection>
      );

    if (activeTab === "privacy")
      return <PrivacyPanel audit={detail.audit} />;

    if (activeTab === "settings")
      return (
        <LockedSection requiredTier="index" currentTier={tier} onUpgrade={goUpgrade}>
          <SettingsPanel
            server={detail.server}
            saving={saving}
            saveError={saveError}
            onSave={saveServer}
            onSavePatch={saveSettingsPatch}
          />
        </LockedSection>
      );

    if (activeTab === "profile")
      return (
        <LockedSection requiredTier="index" currentTier={tier} onUpgrade={goUpgrade}>
          <BotProfilePanel
            server={detail.server}
            saving={saving}
            saveError={saveError}
            onSave={saveServer}
            onUpgrade={goUpgrade}
          />
        </LockedSection>
      );

    return (
      <OverviewPanel
        detail={detail}
        onNavigate={setActiveTab}
        onSavePatch={saveSettingsPatch}
        saving={saving}
        scanRunning={scanRunning}
        onScan={handleScan}
        onUpgrade={goUpgrade}
      />
    );
  }

  if (page === "oauth-callback") {
    return <OAuthCallbackPage />;
  }

  if (page === "auth-finish") {
    return <AuthFinishPage />;
  }

  if (page === "home") return null;

  if (page === "info" || page === "commands") return null;

  if (page === "tos") return null;

  if (page === "privacy-policy") return null;

  if (page === "usage") {
    return <UsagePage me={me} onNavigate={navigate} />;
  }

  if (page === "admin") {
    return (
      <AdminPage
        me={me}
        onNavigate={navigate}
        onOpenServer={(serverId) => {
          // Set the URL with the server ID BEFORE changing page so that
          // when getMe fires (triggered by the page change) it reads the
          // correct ?server= param and doesn't fall back to our own servers.
          window.history.pushState(null, "", `/dashboard?server=${serverId}#overview`);
          setSelectedServerId(serverId);
          setPage("dashboard");
          setActiveTab("overview");
        }}
      />
    );
  }

  if (page === "servers") {
    return (
      <>
        <ServerManagementPage
          me={me}
          loading={loading}
          onNavigate={navigate}
          onManageServer={manageServer}
        />
        {authError && (
          <AuthErrorToast
            dismissing={authToastDismissing}
            onDismiss={dismissAuthToast}
          />
        )}
      </>
    );
  }

  if (page === "billing") {
    return (
      <>
        <BillingPage me={me} onNavigate={navigate} showToast={showToast} />
        {appToast && (
          <Toast
            message={appToast.message}
            tone={appToast.tone}
            icon={appToast.icon}
            dismissing={appToastDismissing}
            onDismiss={dismissAppToast}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="dashboard-outer">
        <div className="dashboard-nav-row">
          <MarketingNav me={me} onNavigate={navigate} />
        </div>
        <main className="shell">
          <aside className="sidebar" aria-label="Kodama dashboard navigation">

          {me && (
            <ServerSelect
              servers={me.servers}
              selectedId={selectedServerId}
              onChange={setSelectedServerId}
            />
          )}

          <nav className="nav" aria-label="Dashboard tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  className={`nav-item ${activeTab === tab.id ? "active" : ""}`}
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon size={18} aria-hidden="true" />
                  {tab.label}
                </button>
              );
            })}
          </nav>

          <div className="sidebar-bottom">
            <button
              className="ghost-button"
              type="button"
              onClick={() => navigate("billing")}
            >
              <CreditCard size={18} aria-hidden="true" />
              Plans &amp; Billing
            </button>

            <button
              className="ghost-button"
              type="button"
              onClick={() => {
                logout().finally(() => {
                  navigate("home");
                });
              }}
            >
              <LogOut size={18} />
              Log out
            </button>
          </div>
        </aside>

        <section className="content">
          <header className="topbar">
            <div>
              <p className="eyebrow">{selectedServer?.name ?? detail?.server.name ?? "Kodama"}</p>
              <h2>{title}</h2>
            </div>
            <div className="refresh-block">
              <span>Last indexed</span>
              <strong>
                {formatDate(detail?.overview.lastIndexedAt ?? null)}
              </strong>
            </div>
            <button
              className="primary-button"
              type="button"
              onClick={() =>
                activeTab === "settings"
                  ? setActiveTab("overview")
                  : setActiveTab("settings")
              }
            >
              <SlidersHorizontal size={18} aria-hidden="true" />
              {activeTab === "settings" ? "Overview" : "Controls"}
            </button>
          </header>

          {me?.isAdmin && detail && !me.servers.some((s) => s.id === detail.server.id) && (
            <div className="admin-impersonation-banner">
              <ShieldCheck size={14} aria-hidden="true" />
              <span>Admin view — <strong>{detail.server.name}</strong> ({detail.server.id})</span>
              <button
                type="button"
                className="admin-impersonation-back"
                onClick={() => navigate("admin")}
              >
                ← Back to admin panel
              </button>
            </div>
          )}

          {loading && !detail ? (
            <EmptyState
              icon={<LoadingDots label="Loading dashboard" />}
              title="Loading dashboard"
              body="Checking your Discord servers and Kodama settings."
            />
          ) : dashboardError ? (
            <EmptyState
              title="Could not load this server"
              body={dashboardError}
              action={
                <button
                  className="primary-button"
                  type="button"
                  onClick={() => setLoadAttempt((attempt) => attempt + 1)}
                >
                  <SlidersHorizontal size={18} aria-hidden="true" />
                  Try again
                </button>
              }
            />
          ) : !detail ? (
            <EmptyState
              title="No manageable servers"
              body="Install Kodama in a server where you have Manage Server permission, then reload."
              action={
                <button
                  className="primary-button"
                  type="button"
                  onClick={() => void initiateLogin(currentReturnTo())}
                >
                  <LogIn size={18} aria-hidden="true" />
                  Login with Discord
                </button>
              }
            />
          ) : (
            renderPanel()
          )}
        </section>
      </main>
      </div>
      {authError && (
        <AuthErrorToast
          dismissing={authToastDismissing}
          onDismiss={dismissAuthToast}
        />
      )}
      {appToast && (
        <Toast
          message={appToast.message}
          tone={appToast.tone}
          icon={appToast.icon}
          dismissing={appToastDismissing}
          onDismiss={dismissAppToast}
        />
      )}
      {detail && (
        <AskKodamaWidget
          open={askOpen}
          serverName={detail.server.name}
          messages={askMessages}
          sending={askSending}
          error={askError}
          onOpen={() => setAskOpen(true)}
          onClose={() => setAskOpen(false)}
          onSend={sendAskQuestion}
        />
      )}
    </>
  );
}

export function mountApp(): void {
  createRoot(document.getElementById("root") as HTMLElement).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}
